/**
 * Shared UI micro-translations for buttons, headings, and labels
 * used across multiple pages. Covers all 16 supported languages.
 */

type L = Record<string, string>;

const t = (map: L, lang: string): string => map[lang] || map.en;

export const UI = {
  bookACall: (lang: string) => t({
    en: 'Book a Call', fr: 'Réserver un appel', es: 'Reservar llamada', ar: 'احجز مكالمة',
    de: 'Anruf buchen', it: 'Prenota una chiamata', pt: 'Agendar chamada', nl: 'Bel plannen',
    pl: 'Zarezerwuj rozmowę', ru: 'Забронировать звонок', zh: '预约通话', ja: '通話を予約',
    tr: 'Arama Rezerve Et', ko: '전화 예약', sv: 'Boka samtal', da: 'Book et opkald',
  }, lang),

  viewCaseStudies: (lang: string) => t({
    en: 'View Case Studies', fr: 'Voir études de cas', es: 'Ver casos de éxito', ar: 'عرض دراسات الحالة',
    de: 'Fallstudien ansehen', it: 'Vedi casi studio', pt: 'Ver estudos de caso', nl: 'Casestudies bekijken',
    pl: 'Zobacz studia przypadków', ru: 'Посмотреть кейсы', zh: '查看案例研究', ja: 'ケーススタディを見る',
    tr: 'Vaka Çalışmalarını Gör', ko: '사례 연구 보기', sv: 'Visa fallstudier', da: 'Se casestudier',
  }, lang),

  viewAllCaseStudies: (lang: string) => t({
    en: 'View All Case Studies', fr: 'Voir toutes les études de cas', es: 'Ver todos los casos', ar: 'عرض جميع دراسات الحالة',
    de: 'Alle Fallstudien ansehen', it: 'Vedi tutti i casi studio', pt: 'Ver todos os estudos', nl: 'Alle casestudies bekijken',
    pl: 'Zobacz wszystkie studia', ru: 'Все кейсы', zh: '查看所有案例', ja: 'すべてのケーススタディ',
    tr: 'Tüm Vaka Çalışmaları', ko: '모든 사례 연구', sv: 'Visa alla fallstudier', da: 'Se alle casestudier',
  }, lang),

  challengesWeSolve: (lang: string) => t({
    en: 'Challenges We Solve', fr: 'Défis que nous résolvons', es: 'Desafíos que resolvemos', ar: 'التحديات التي نحلها',
    de: 'Herausforderungen die wir lösen', it: 'Sfide che risolviamo', pt: 'Desafios que resolvemos', nl: 'Uitdagingen die we oplossen',
    pl: 'Wyzwania które rozwiązujemy', ru: 'Вызовы которые мы решаем', zh: '我们解决的挑战', ja: '私たちが解決する課題',
    tr: 'Çözdüğümüz Zorluklar', ko: '우리가 해결하는 과제', sv: 'Utmaningar vi löser', da: 'Udfordringer vi løser',
  }, lang),

  servicesForIndustry: (lang: string) => t({
    en: 'Services for This Industry', fr: 'Services pour cette industrie', es: 'Servicios para esta industria', ar: 'خدمات لهذه الصناعة',
    de: 'Dienste für diese Branche', it: 'Servizi per questo settore', pt: 'Serviços para esta indústria', nl: 'Diensten voor deze sector',
    pl: 'Usługi dla tej branży', ru: 'Услуги для этой отрасли', zh: '该行业服务', ja: 'この業界向けサービス',
    tr: 'Bu Sektör İçin Hizmetler', ko: '이 산업을 위한 서비스', sv: 'Tjänster för denna bransch', da: 'Tjenester til denne branche',
  }, lang),

  typicalKPIs: (lang: string) => t({
    en: 'Typical KPIs', fr: 'KPIs typiques', es: 'KPIs típicos', ar: 'مؤشرات الأداء النموذجية',
    de: 'Typische KPIs', it: 'KPI tipici', pt: 'KPIs típicos', nl: 'Typische KPIs',
    pl: 'Typowe KPI', ru: 'Типичные KPI', zh: '典型KPI', ja: '典型的なKPI',
    tr: 'Tipik KPI\'lar', ko: '일반적인 KPI', sv: 'Typiska KPI:er', da: 'Typiske KPI\'er',
  }, lang),

  readyToTransform: (lang: string) => t({
    en: 'Ready to Transform Your Support?', fr: 'Prêt à transformer votre support ?', es: '¿Listo para transformar tu soporte?', ar: 'هل أنت مستعد لتحويل دعمك؟',
    de: 'Bereit Ihren Support zu transformieren?', it: 'Pronto a trasformare il tuo supporto?', pt: 'Pronto para transformar seu suporte?', nl: 'Klaar om uw support te transformeren?',
    pl: 'Gotowy na transformację wsparcia?', ru: 'Готовы трансформировать поддержку?', zh: '准备好转变您的支持了吗？', ja: 'サポートを変革する準備はできていますか？',
    tr: 'Desteğinizi Dönüştürmeye Hazır mısınız?', ko: '지원을 혁신할 준비가 되셨나요?', sv: 'Redo att förvandla er support?', da: 'Klar til at transformere din support?',
  }, lang),

  startWithPilot: (lang: string) => t({
    en: 'Start with a 7-day pilot and see the results.', fr: 'Commencez par un pilote de 7 jours et voyez les résultats.', es: 'Comienza con un piloto de 7 días y ve los resultados.', ar: 'ابدأ بتجربة 7 أيام وشاهد النتائج.',
    de: 'Starten Sie mit einem 7-Tage-Pilot und sehen Sie die Ergebnisse.', it: 'Inizia con un pilota di 7 giorni e vedi i risultati.', pt: 'Comece com um piloto de 7 dias e veja os resultados.', nl: 'Begin met een 7-daagse pilot en zie de resultaten.',
    pl: 'Zacznij od 7-dniowego pilota i zobacz wyniki.', ru: 'Начните с 7-дневного пилота и увидьте результаты.', zh: '从7天试点开始，看看效果。', ja: '7日間のパイロットから始めて、結果をご確認ください。',
    tr: '7 günlük pilotla başlayın ve sonuçları görün.', ko: '7일 파일럿으로 시작하고 결과를 확인하세요.', sv: 'Börja med en 7-dagars pilot och se resultaten.', da: 'Start med en 7-dages pilot og se resultaterne.',
  }, lang),

  startPilot: (lang: string) => t({
    en: 'Start Pilot', fr: 'Démarrer le pilote', es: 'Iniciar piloto', ar: 'ابدأ التجربة',
    de: 'Pilot starten', it: 'Avvia pilota', pt: 'Iniciar piloto', nl: 'Pilot starten',
    pl: 'Rozpocznij pilota', ru: 'Начать пилот', zh: '开始试点', ja: 'パイロット開始',
    tr: 'Pilotu Başlat', ko: '파일럿 시작', sv: 'Starta pilot', da: 'Start pilot',
  }, lang),

  readyToPartner: (lang: string) => t({
    en: 'Ready to Partner?', fr: 'Prêt à Collaborer ?', es: '¿Listo para Colaborar?', ar: 'مستعد للتعاون؟',
    de: 'Bereit zur Partnerschaft?', it: 'Pronti a collaborare?', pt: 'Pronto para parceria?', nl: 'Klaar voor samenwerking?',
    pl: 'Gotowy na współpracę?', ru: 'Готовы к сотрудничеству?', zh: '准备好合作了吗？', ja: 'パートナーシップの準備はできていますか？',
    tr: 'Ortaklığa Hazır mısınız?', ko: '파트너십 준비가 되셨나요?', sv: 'Redo för partnerskap?', da: 'Klar til partnerskab?',
  }, lang),

  scheduleMoroccoCall: (lang: string) => t({
    en: 'Schedule a call to discuss how our Morocco operations can support your business goals.',
    fr: 'Planifiez un appel pour discuter de comment nos opérations au Maroc peuvent soutenir vos objectifs.',
    es: 'Programa una llamada para discutir cómo nuestras operaciones en Marruecos pueden apoyar tus objetivos.',
    ar: 'حدد موعدًا لمكالمة لمناقشة كيف يمكن لعملياتنا في المغرب دعم أهدافك.',
    de: 'Planen Sie einen Anruf um zu besprechen wie unsere Marokko-Operationen Ihre Geschäftsziele unterstützen können.',
    it: 'Pianifica una chiamata per discutere come le nostre operazioni in Marocco possono supportare i tuoi obiettivi.',
    pt: 'Agende uma chamada para discutir como nossas operações no Marrocos podem apoiar seus objetivos.',
    nl: 'Plan een gesprek om te bespreken hoe onze Marokko-operaties uw bedrijfsdoelen kunnen ondersteunen.',
    pl: 'Zaplanuj rozmowę aby omówić jak nasze operacje w Maroku mogą wspierać Twoje cele biznesowe.',
    ru: 'Запланируйте звонок чтобы обсудить как наши операции в Марокко могут поддержать ваши бизнес-цели.',
    zh: '预约电话讨论我们在摩洛哥的运营如何支持您的业务目标。',
    ja: 'モロッコの事業がビジネス目標をどのようにサポートできるか、お電話でご相談ください。',
    tr: 'Fas operasyonlarımızın iş hedeflerinizi nasıl destekleyebileceğini görüşmek için bir arama planlayın.',
    ko: '모로코 운영이 비즈니스 목표를 어떻게 지원할 수 있는지 논의하기 위해 전화를 예약하세요.',
    sv: 'Boka ett samtal för att diskutera hur våra Marocko-verksamheter kan stödja era affärsmål.',
    da: 'Book et opkald for at diskutere hvordan vores Marokko-operationer kan støtte dine forretningsmål.',
  }, lang),

  startAPilot: (lang: string) => t({
    en: 'Start a Pilot', fr: 'Démarrer un Pilote', es: 'Iniciar Piloto', ar: 'ابدأ تجربة',
    de: 'Pilot starten', it: 'Avvia un Pilota', pt: 'Iniciar Piloto', nl: 'Start een Pilot',
    pl: 'Rozpocznij Pilota', ru: 'Начать Пилот', zh: '开始试点', ja: 'パイロット開始',
    tr: 'Pilot Başlat', ko: '파일럿 시작', sv: 'Starta Pilot', da: 'Start Pilot',
  }, lang),

  // === Hero Section ===
  aiBadge: (lang: string) => t({
    en: 'AI-Powered BPO & Digital Solutions', fr: 'BPO & Solutions Digitales propulsés par l\'IA', es: 'BPO y Soluciones Digitales con IA', ar: 'حلول BPO والحلول الرقمية المدعومة بالذكاء الاصطناعي',
    de: 'KI-gestützte BPO & Digitale Lösungen', it: 'BPO & Soluzioni Digitali con IA', pt: 'BPO & Soluções Digitais com IA', nl: 'AI-gestuurde BPO & Digitale Oplossingen',
    pl: 'BPO i Rozwiązania Cyfrowe z AI', ru: 'BPO и цифровые решения на базе ИИ', zh: 'AI驱动的BPO和数字解决方案', ja: 'AI搭載BPO＆デジタルソリューション',
    tr: 'AI Destekli BPO & Dijital Çözümler', ko: 'AI 기반 BPO 및 디지털 솔루션', sv: 'AI-drivna BPO & Digitala Lösningar', da: 'AI-drevet BPO & Digitale Løsninger',
  }, lang),

  heroSubtitle: (lang: string) => t({
    en: 'Globally, Seamlessly', fr: 'Mondialement, Sans Effort', es: 'Global, Sin Fisuras', ar: 'عالمياً، بسلاسة',
    de: 'Global, Nahtlos', it: 'Globalmente, Senza Interruzioni', pt: 'Globalmente, Sem Esforço', nl: 'Wereldwijd, Naadloos',
    pl: 'Globalnie, Bezproblemowo', ru: 'Глобально, Бесшовно', zh: '全球化，无缝衔接', ja: 'グローバルに、シームレスに',
    tr: 'Küresel, Sorunsuz', ko: '글로벌, 원활하게', sv: 'Globalt, Sömlöst', da: 'Globalt, Problemfrit',
  }, lang),

  startFreeWeek: (lang: string) => t({
    en: '🎁 Start Free Week', fr: '🎁 Semaine d\'essai gratuite', es: '🎁 Semana gratis', ar: '🎁 ابدأ أسبوعك المجاني',
    de: '🎁 Kostenlose Woche starten', it: '🎁 Settimana gratuita', pt: '🎁 Semana grátis', nl: '🎁 Gratis proefweek',
    pl: '🎁 Darmowy tydzień próbny', ru: '🎁 Бесплатная неделя', zh: '🎁 开始免费体验周', ja: '🎁 無料お試し週間',
    tr: '🎁 Ücretsiz Hafta Başlat', ko: '🎁 무료 체험 주간', sv: '🎁 Starta gratis vecka', da: '🎁 Start gratis uge',
  }, lang),

  orderITServices: (lang: string) => t({
    en: 'Order IT Services', fr: 'Commander des services IT', es: 'Solicitar servicios IT', ar: 'اطلب خدمات تكنولوجيا المعلومات',
    de: 'IT-Services bestellen', it: 'Ordina servizi IT', pt: 'Solicitar serviços de TI', nl: 'IT-diensten bestellen',
    pl: 'Zamów usługi IT', ru: 'Заказать ИТ-услуги', zh: '订购IT服务', ja: 'ITサービスを注文',
    tr: 'BT Hizmetleri Sipariş Et', ko: 'IT 서비스 주문', sv: 'Beställ IT-tjänster', da: 'Bestil IT-tjenester',
  }, lang),

  trustBadge: (lang: string) => t({
    en: 'First week free • No commitment • Cancel anytime', fr: 'Première semaine gratuite • Sans engagement • Annulez à tout moment', es: 'Primera semana gratis • Sin compromiso • Cancela cuando quieras', ar: 'الأسبوع الأول مجاني • بدون التزام • إلغاء في أي وقت',
    de: 'Erste Woche kostenlos • Keine Bindung • Jederzeit kündbar', it: 'Prima settimana gratis • Senza impegno • Cancella quando vuoi', pt: 'Primeira semana grátis • Sem compromisso • Cancele a qualquer momento', nl: 'Eerste week gratis • Geen verplichting • Altijd opzegbaar',
    pl: 'Pierwszy tydzień za darmo • Bez zobowiązań • Anuluj w każdej chwili', ru: 'Первая неделя бесплатно • Без обязательств • Отмена в любое время', zh: '首周免费 • 无需承诺 • 随时取消', ja: '初週無料 • 契約不要 • いつでもキャンセル可能',
    tr: 'İlk hafta ücretsiz • Taahhüt yok • İstediğiniz zaman iptal', ko: '첫 주 무료 • 약정 없음 • 언제든 취소 가능', sv: 'Första veckan gratis • Inget åtagande • Avbryt när som helst', da: 'Første uge gratis • Ingen binding • Opsig når som helst',
  }, lang),

  agentsWorldwide: (lang: string) => t({
    en: 'Agents Worldwide', fr: 'Agents dans le monde', es: 'Agentes en el mundo', ar: 'وكلاء حول العالم',
    de: 'Agenten weltweit', it: 'Agenti nel mondo', pt: 'Agentes no mundo', nl: 'Agenten wereldwijd',
    pl: 'Agenci na świecie', ru: 'Агенты по всему миру', zh: '全球代理', ja: '世界中のエージェント',
    tr: 'Dünya genelinde ajanlar', ko: '전 세계 에이전트', sv: 'Agenter världen över', da: 'Agenter verden over',
  }, lang),

  supportCoverage: (lang: string) => t({
    en: 'Support Coverage', fr: 'Couverture support', es: 'Cobertura de soporte', ar: 'تغطية الدعم',
    de: 'Support-Abdeckung', it: 'Copertura supporto', pt: 'Cobertura de suporte', nl: 'Ondersteuningsdekking',
    pl: 'Pokrycie wsparcia', ru: 'Покрытие поддержки', zh: '支持覆盖', ja: 'サポートカバレッジ',
    tr: 'Destek Kapsamı', ko: '지원 범위', sv: 'Supporttäckning', da: 'Supportdækning',
  }, lang),

  languages: (lang: string) => t({
    en: 'Languages', fr: 'Langues', es: 'Idiomas', ar: 'اللغات',
    de: 'Sprachen', it: 'Lingue', pt: 'Idiomas', nl: 'Talen',
    pl: 'Języki', ru: 'Языки', zh: '语言', ja: '言語',
    tr: 'Diller', ko: '언어', sv: 'Språk', da: 'Sprog',
  }, lang),

  uptimeSLA: (lang: string) => t({
    en: 'Uptime SLA', fr: 'SLA de disponibilité', es: 'SLA de disponibilidad', ar: 'اتفاقية مستوى الخدمة',
    de: 'Uptime-SLA', it: 'SLA di uptime', pt: 'SLA de disponibilidade', nl: 'Uptime SLA',
    pl: 'SLA dostępności', ru: 'SLA доступности', zh: '运行时间SLA', ja: '稼働時間SLA',
    tr: 'Çalışma Süresi SLA', ko: '가동 시간 SLA', sv: 'Drifttids-SLA', da: 'Oppetids-SLA',
  }, lang),

  activeAgents: (lang: string) => t({
    en: 'Active Agents', fr: 'Agents actifs', es: 'Agentes activos', ar: 'وكلاء نشطون',
    de: 'Aktive Agenten', it: 'Agenti attivi', pt: 'Agentes ativos', nl: 'Actieve agenten',
    pl: 'Aktywni agenci', ru: 'Активные агенты', zh: '活跃代理', ja: 'アクティブエージェント',
    tr: 'Aktif Ajanlar', ko: '활동 중인 에이전트', sv: 'Aktiva agenter', da: 'Aktive agenter',
  }, lang),

  onlineNow: (lang: string) => t({
    en: 'Online Now', fr: 'En ligne maintenant', es: 'En línea ahora', ar: 'متصل الآن',
    de: 'Jetzt online', it: 'Online ora', pt: 'Online agora', nl: 'Nu online',
    pl: 'Online teraz', ru: 'Сейчас онлайн', zh: '现在在线', ja: '現在オンライン',
    tr: 'Şimdi Çevrimiçi', ko: '현재 온라인', sv: 'Online nu', da: 'Online nu',
  }, lang),

  supported: (lang: string) => t({
    en: 'Supported', fr: 'Supportées', es: 'Soportados', ar: 'مدعومة',
    de: 'Unterstützt', it: 'Supportate', pt: 'Suportados', nl: 'Ondersteund',
    pl: 'Obsługiwane', ru: 'Поддерживается', zh: '已支持', ja: 'サポート対象',
    tr: 'Destekleniyor', ko: '지원됨', sv: 'Stöds', da: 'Understøttet',
  }, lang),

  // === FAQ Section ===
  faqTitle: (lang: string) => t({
    en: 'Frequently Asked Questions', fr: 'Questions Fréquentes', es: 'Preguntas Frecuentes', ar: 'الأسئلة الشائعة',
    de: 'Häufig gestellte Fragen', it: 'Domande Frequenti', pt: 'Perguntas Frequentes', nl: 'Veelgestelde Vragen',
    pl: 'Często Zadawane Pytania', ru: 'Часто задаваемые вопросы', zh: '常见问题', ja: 'よくある質問',
    tr: 'Sık Sorulan Sorular', ko: '자주 묻는 질문', sv: 'Vanliga Frågor', da: 'Ofte Stillede Spørgsmål',
  }, lang),

  // === Trust Section ===
  informationSecurity: (lang: string) => t({
    en: 'Information Security', fr: 'Sécurité de l\'information', es: 'Seguridad de la información', ar: 'أمن المعلومات',
    de: 'Informationssicherheit', it: 'Sicurezza delle informazioni', pt: 'Segurança da informação', nl: 'Informatiebeveiliging',
    pl: 'Bezpieczeństwo informacji', ru: 'Информационная безопасность', zh: '信息安全', ja: '情報セキュリティ',
    tr: 'Bilgi Güvenliği', ko: '정보 보안', sv: 'Informationssäkerhet', da: 'Informationssikkerhed',
  }, lang),

  dataProtection: (lang: string) => t({
    en: 'Data Protection', fr: 'Protection des données', es: 'Protección de datos', ar: 'حماية البيانات',
    de: 'Datenschutz', it: 'Protezione dei dati', pt: 'Proteção de dados', nl: 'Gegevensbescherming',
    pl: 'Ochrona danych', ru: 'Защита данных', zh: '数据保护', ja: 'データ保護',
    tr: 'Veri Koruma', ko: '데이터 보호', sv: 'Dataskydd', da: 'Databeskyttelse',
  }, lang),

  paymentSecurity: (lang: string) => t({
    en: 'Payment Security', fr: 'Sécurité des paiements', es: 'Seguridad de pagos', ar: 'أمن الدفع',
    de: 'Zahlungssicherheit', it: 'Sicurezza dei pagamenti', pt: 'Segurança de pagamentos', nl: 'Betalingsbeveiliging',
    pl: 'Bezpieczeństwo płatności', ru: 'Безопасность платежей', zh: '支付安全', ja: '決済セキュリティ',
    tr: 'Ödeme Güvenliği', ko: '결제 보안', sv: 'Betalningssäkerhet', da: 'Betalingssikkerhed',
  }, lang),

  hqMorocco: (lang: string) => t({
    en: 'HQ Morocco', fr: 'Siège Maroc', es: 'Sede Marruecos', ar: 'المقر المغرب',
    de: 'HQ Marokko', it: 'Sede Marocco', pt: 'Sede Marrocos', nl: 'HQ Marokko',
    pl: 'Siedziba Maroko', ru: 'Штаб-квартира Марокко', zh: '摩洛哥总部', ja: 'モロッコ本社',
    tr: 'Fas Merkez', ko: '모로코 본사', sv: 'HK Marocko', da: 'HQ Marokko',
  }, lang),

  serviceRegions: (lang: string) => t({
    en: 'Service Regions', fr: 'Régions desservies', es: 'Regiones de servicio', ar: 'مناطق الخدمة',
    de: 'Serviceregionen', it: 'Regioni servite', pt: 'Regiões de serviço', nl: 'Serviceregio\'s',
    pl: 'Regiony obsługi', ru: 'Регионы обслуживания', zh: '服务区域', ja: 'サービス地域',
    tr: 'Hizmet Bölgeleri', ko: '서비스 지역', sv: 'Serviceregioner', da: 'Serviceregioner',
  }, lang),

  // === ROI Calculator ===
  roiCalculator: (lang: string) => t({
    en: 'ROI Calculator', fr: 'Calculateur ROI', es: 'Calculadora ROI', ar: 'حاسبة العائد على الاستثمار',
    de: 'ROI-Rechner', it: 'Calcolatore ROI', pt: 'Calculadora ROI', nl: 'ROI-calculator',
    pl: 'Kalkulator ROI', ru: 'Калькулятор ROI', zh: 'ROI计算器', ja: 'ROI計算ツール',
    tr: 'ROI Hesaplayıcı', ko: 'ROI 계산기', sv: 'ROI-kalkylator', da: 'ROI-beregner',
  }, lang),

  perMonthAt: (lang: string) => t({
    en: 'per month @ $35/hr', fr: 'par mois à 35€/h', es: 'por mes a $35/h', ar: 'شهرياً بسعر 35$/ساعة',
    de: 'pro Monat bei 35€/Std.', it: 'al mese a 35€/h', pt: 'por mês a $35/h', nl: 'per maand @ €35/uur',
    pl: 'miesięcznie przy $35/h', ru: 'в месяц при $35/ч', zh: '每月 @ $35/小时', ja: '月額 @ $35/時間',
    tr: 'aylık @ $35/saat', ko: '월 @ $35/시간', sv: 'per månad @ $35/tim', da: 'pr. måned @ $35/time',
  }, lang),

  upToSavings: (lang: string, pct: number) => t({
    en: `Up to ${pct}% savings`, fr: `Jusqu'à ${pct}% d'économies`, es: `Hasta ${pct}% de ahorro`, ar: `توفير يصل إلى ${pct}%`,
    de: `Bis zu ${pct}% Einsparung`, it: `Fino al ${pct}% di risparmio`, pt: `Até ${pct}% de economia`, nl: `Tot ${pct}% besparing`,
    pl: `Do ${pct}% oszczędności`, ru: `До ${pct}% экономии`, zh: `最高节省${pct}%`, ja: `最大${pct}%の節約`,
    tr: `%${pct}'e kadar tasarruf`, ko: `최대 ${pct}% 절감`, sv: `Upp till ${pct}% besparing`, da: `Op til ${pct}% besparelse`,
  }, lang),

  startingFrom: (lang: string) => t({
    en: 'Starting from €3/hr', fr: 'À partir de 3€/h', es: 'Desde €3/h', ar: 'ابتداءً من 3€/ساعة',
    de: 'Ab 3€/Std.', it: 'A partire da 3€/h', pt: 'A partir de €3/h', nl: 'Vanaf €3/uur',
    pl: 'Od €3/h', ru: 'От €3/ч', zh: '最低€3/小时', ja: '€3/時間〜',
    tr: '€3/saatten başlayan', ko: '€3/시간부터', sv: 'Från €3/tim', da: 'Fra €3/time',
  }, lang),

  significantSavings: (lang: string) => t({
    en: 'Significant savings', fr: 'Économies significatives', es: 'Ahorros significativos', ar: 'توفير كبير',
    de: 'Erhebliche Einsparungen', it: 'Risparmi significativi', pt: 'Economia significativa', nl: 'Aanzienlijke besparingen',
    pl: 'Znaczne oszczędności', ru: 'Значительная экономия', zh: '显著节省', ja: '大幅なコスト削減',
    tr: 'Önemli tasarruflar', ko: '상당한 절감', sv: 'Betydande besparingar', da: 'Betydelige besparelser',
  }, lang),

  // === Contact Form ===
  inboundSupport: (lang: string) => t({
    en: 'Inbound Support', fr: 'Support entrant', es: 'Soporte entrante', ar: 'دعم وارد',
    de: 'Inbound-Support', it: 'Supporto in entrata', pt: 'Suporte receptivo', nl: 'Inbound support',
    pl: 'Wsparcie przychodzące', ru: 'Входящая поддержка', zh: '呼入支持', ja: 'インバウンドサポート',
    tr: 'Gelen Destek', ko: '인바운드 지원', sv: 'Inkommande support', da: 'Indgående support',
  }, lang),

  outboundSales: (lang: string) => t({
    en: 'Outbound Sales', fr: 'Ventes sortantes', es: 'Ventas salientes', ar: 'مبيعات صادرة',
    de: 'Outbound-Vertrieb', it: 'Vendite outbound', pt: 'Vendas ativas', nl: 'Outbound sales',
    pl: 'Sprzedaż wychodząca', ru: 'Исходящие продажи', zh: '外呼销售', ja: 'アウトバウンドセールス',
    tr: 'Giden Satış', ko: '아웃바운드 영업', sv: 'Utgående försäljning', da: 'Udgående salg',
  }, lang),

  technicalHelpdesk: (lang: string) => t({
    en: 'Technical Helpdesk', fr: 'Support technique', es: 'Soporte técnico', ar: 'مكتب المساعدة التقنية',
    de: 'Technischer Helpdesk', it: 'Helpdesk tecnico', pt: 'Helpdesk técnico', nl: 'Technische helpdesk',
    pl: 'Helpdesk techniczny', ru: 'Техническая поддержка', zh: '技术服务台', ja: 'テクニカルヘルプデスク',
    tr: 'Teknik Yardım Masası', ko: '기술 헬프데스크', sv: 'Teknisk helpdesk', da: 'Teknisk helpdesk',
  }, lang),

  backOffice: (lang: string) => t({
    en: 'Back Office', fr: 'Back-office', es: 'Back-office', ar: 'المكتب الخلفي',
    de: 'Backoffice', it: 'Back-office', pt: 'Back-office', nl: 'Backoffice',
    pl: 'Back-office', ru: 'Бэк-офис', zh: '后台办公', ja: 'バックオフィス',
    tr: 'Arka Ofis', ko: '백오피스', sv: 'Backoffice', da: 'Backoffice',
  }, lang),

  webDevelopment: (lang: string) => t({
    en: 'Web Development', fr: 'Développement web', es: 'Desarrollo web', ar: 'تطوير الويب',
    de: 'Webentwicklung', it: 'Sviluppo web', pt: 'Desenvolvimento web', nl: 'Webontwikkeling',
    pl: 'Tworzenie stron', ru: 'Веб-разработка', zh: 'Web开发', ja: 'Web開発',
    tr: 'Web Geliştirme', ko: '웹 개발', sv: 'Webbutveckling', da: 'Webudvikling',
  }, lang),

  mobileApps: (lang: string) => t({
    en: 'Mobile Apps', fr: 'Applications mobiles', es: 'Aplicaciones móviles', ar: 'تطبيقات الجوال',
    de: 'Mobile Apps', it: 'App mobile', pt: 'Apps móveis', nl: 'Mobiele apps',
    pl: 'Aplikacje mobilne', ru: 'Мобильные приложения', zh: '移动应用', ja: 'モバイルアプリ',
    tr: 'Mobil Uygulamalar', ko: '모바일 앱', sv: 'Mobilappar', da: 'Mobile apps',
  }, lang),

  aiChatbots: (lang: string) => t({
    en: 'AI Chatbots', fr: 'Chatbots IA', es: 'Chatbots IA', ar: 'روبوتات الدردشة بالذكاء الاصطناعي',
    de: 'KI-Chatbots', it: 'Chatbot IA', pt: 'Chatbots com IA', nl: 'AI-chatbots',
    pl: 'Chatboty AI', ru: 'ИИ-чатботы', zh: 'AI聊天机器人', ja: 'AIチャットボット',
    tr: 'AI Chatbotları', ko: 'AI 챗봇', sv: 'AI-chattbottar', da: 'AI-chatbots',
  }, lang),

  crmIntegration: (lang: string) => t({
    en: 'CRM Integration', fr: 'Intégration CRM', es: 'Integración CRM', ar: 'تكامل CRM',
    de: 'CRM-Integration', it: 'Integrazione CRM', pt: 'Integração CRM', nl: 'CRM-integratie',
    pl: 'Integracja CRM', ru: 'Интеграция CRM', zh: 'CRM集成', ja: 'CRM統合',
    tr: 'CRM Entegrasyonu', ko: 'CRM 통합', sv: 'CRM-integration', da: 'CRM-integration',
  }, lang),

  // === Footer ===
  stayUpdated: (lang: string) => t({
    en: 'Stay Updated with Industry Insights', fr: 'Restez informé des tendances du secteur', es: 'Mantente al día con las tendencias', ar: 'ابقَ على اطلاع بأحدث الاتجاهات',
    de: 'Bleiben Sie über Branchentrends informiert', it: 'Resta aggiornato sulle tendenze', pt: 'Fique atualizado com insights do setor', nl: 'Blijf op de hoogte van trends',
    pl: 'Bądź na bieżąco z trendami', ru: 'Будьте в курсе отраслевых трендов', zh: '了解行业最新动态', ja: '業界の最新情報を入手',
    tr: 'Sektör trendlerinden haberdar olun', ko: '업계 인사이트를 받아보세요', sv: 'Håll dig uppdaterad med branschtrender', da: 'Hold dig opdateret med branchetrends',
  }, lang),

  newsletterDesc: (lang: string) => t({
    en: 'Get the latest BPO trends, customer experience tips, and exclusive offers delivered to your inbox.',
    fr: 'Recevez les dernières tendances BPO, conseils en expérience client et offres exclusives.',
    es: 'Recibe las últimas tendencias de BPO, consejos de experiencia del cliente y ofertas exclusivas.',
    ar: 'احصل على أحدث اتجاهات BPO ونصائح تجربة العملاء والعروض الحصرية.',
    de: 'Erhalten Sie die neuesten BPO-Trends, CX-Tipps und exklusive Angebote.',
    it: 'Ricevi le ultime tendenze BPO, consigli sulla CX e offerte esclusive.',
    pt: 'Receba as últimas tendências de BPO, dicas de CX e ofertas exclusivas.',
    nl: 'Ontvang de nieuwste BPO-trends, CX-tips en exclusieve aanbiedingen.',
    pl: 'Otrzymuj najnowsze trendy BPO, porady CX i ekskluzywne oferty.',
    ru: 'Получайте последние тренды BPO, советы по CX и эксклюзивные предложения.',
    zh: '获取最新的BPO趋势、客户体验技巧和独家优惠。',
    ja: '最新のBPOトレンド、CXのヒント、限定オファーをお届けします。',
    tr: 'En son BPO trendlerini, CX ipuçlarını ve özel teklifleri alın.',
    ko: '최신 BPO 트렌드, CX 팁, 독점 혜택을 받아보세요.',
    sv: 'Få de senaste BPO-trenderna, CX-tips och exklusiva erbjudanden.',
    da: 'Få de seneste BPO-trends, CX-tips og eksklusive tilbud.',
  }, lang),

  enterEmail: (lang: string) => t({
    en: 'Enter your email', fr: 'Votre email', es: 'Tu email', ar: 'أدخل بريدك الإلكتروني',
    de: 'Ihre E-Mail', it: 'La tua email', pt: 'Seu email', nl: 'Uw email',
    pl: 'Twój email', ru: 'Ваш email', zh: '输入邮箱', ja: 'メールアドレス',
    tr: 'E-postanız', ko: '이메일 입력', sv: 'Din e-post', da: 'Din e-mail',
  }, lang),

  subscribe: (lang: string) => t({
    en: 'Subscribe', fr: 'S\'abonner', es: 'Suscribirse', ar: 'اشتراك',
    de: 'Abonnieren', it: 'Iscriviti', pt: 'Inscrever-se', nl: 'Abonneren',
    pl: 'Subskrybuj', ru: 'Подписаться', zh: '订阅', ja: '購読',
    tr: 'Abone Ol', ko: '구독', sv: 'Prenumerera', da: 'Abonnér',
  }, lang),

  subscribed: (lang: string) => t({
    en: 'Subscribed', fr: 'Abonné', es: 'Suscrito', ar: 'تم الاشتراك',
    de: 'Abonniert', it: 'Iscritto', pt: 'Inscrito', nl: 'Geabonneerd',
    pl: 'Subskrybowano', ru: 'Подписано', zh: '已订阅', ja: '購読済み',
    tr: 'Abone olundu', ko: '구독됨', sv: 'Prenumererad', da: 'Abonneret',
  }, lang),

  footerServices: (lang: string) => t({
    en: 'Services', fr: 'Services', es: 'Servicios', ar: 'الخدمات',
    de: 'Dienstleistungen', it: 'Servizi', pt: 'Serviços', nl: 'Diensten',
    pl: 'Usługi', ru: 'Услуги', zh: '服务', ja: 'サービス',
    tr: 'Hizmetler', ko: '서비스', sv: 'Tjänster', da: 'Tjenester',
  }, lang),

  footerCompany: (lang: string) => t({
    en: 'Company', fr: 'Entreprise', es: 'Empresa', ar: 'الشركة',
    de: 'Unternehmen', it: 'Azienda', pt: 'Empresa', nl: 'Bedrijf',
    pl: 'Firma', ru: 'Компания', zh: '公司', ja: '会社',
    tr: 'Şirket', ko: '회사', sv: 'Företag', da: 'Virksomhed',
  }, lang),

  footerResources: (lang: string) => t({
    en: 'Resources', fr: 'Ressources', es: 'Recursos', ar: 'الموارد',
    de: 'Ressourcen', it: 'Risorse', pt: 'Recursos', nl: 'Bronnen',
    pl: 'Zasoby', ru: 'Ресурсы', zh: '资源', ja: 'リソース',
    tr: 'Kaynaklar', ko: '리소스', sv: 'Resurser', da: 'Ressourcer',
  }, lang),

  footerLegal: (lang: string) => t({
    en: 'Legal', fr: 'Juridique', es: 'Legal', ar: 'قانوني',
    de: 'Rechtliches', it: 'Legale', pt: 'Jurídico', nl: 'Juridisch',
    pl: 'Prawne', ru: 'Правовая информация', zh: '法律', ja: '法務',
    tr: 'Yasal', ko: '법적', sv: 'Juridiskt', da: 'Juridisk',
  }, lang),

  footerContact: (lang: string) => t({
    en: 'Contact', fr: 'Contact', es: 'Contacto', ar: 'اتصل بنا',
    de: 'Kontakt', it: 'Contatto', pt: 'Contato', nl: 'Contact',
    pl: 'Kontakt', ru: 'Контакты', zh: '联系我们', ja: 'お問い合わせ',
    tr: 'İletişim', ko: '연락처', sv: 'Kontakt', da: 'Kontakt',
  }, lang),

  // Footer links
  bpoCallCenter: (lang: string) => t({
    en: 'BPO & Call Center', fr: 'BPO & Centre d\'appels', es: 'BPO & Centro de llamadas', ar: 'BPO ومركز الاتصال',
    de: 'BPO & Callcenter', it: 'BPO & Call Center', pt: 'BPO & Call Center', nl: 'BPO & Callcenter',
    pl: 'BPO & Call Center', ru: 'BPO и колл-центр', zh: 'BPO & 呼叫中心', ja: 'BPO & コールセンター',
    tr: 'BPO & Çağrı Merkezi', ko: 'BPO & 콜센터', sv: 'BPO & Callcenter', da: 'BPO & Callcenter',
  }, lang),

  chatAndEmail: (lang: string) => t({
    en: 'Chat & Email', fr: 'Chat & Email', es: 'Chat y Email', ar: 'الدردشة والبريد الإلكتروني',
    de: 'Chat & E-Mail', it: 'Chat & Email', pt: 'Chat e Email', nl: 'Chat & E-mail',
    pl: 'Chat i Email', ru: 'Чат и Email', zh: '聊天和邮件', ja: 'チャット＆メール',
    tr: 'Chat & E-posta', ko: '채팅 & 이메일', sv: 'Chatt & E-post', da: 'Chat & E-mail',
  }, lang),

  contentModeration: (lang: string) => t({
    en: 'Content Moderation', fr: 'Modération de contenu', es: 'Moderación de contenido', ar: 'إدارة المحتوى',
    de: 'Content-Moderation', it: 'Moderazione contenuti', pt: 'Moderação de conteúdo', nl: 'Contentmoderatie',
    pl: 'Moderacja treści', ru: 'Модерация контента', zh: '内容审核', ja: 'コンテンツモデレーション',
    tr: 'İçerik Moderasyonu', ko: '콘텐츠 모더레이션', sv: 'Innehållsmoderering', da: 'Indholdsmoderering',
  }, lang),

  digitalDevelopment: (lang: string) => t({
    en: 'Digital Development', fr: 'Développement digital', es: 'Desarrollo digital', ar: 'التطوير الرقمي',
    de: 'Digitale Entwicklung', it: 'Sviluppo digitale', pt: 'Desenvolvimento digital', nl: 'Digitale ontwikkeling',
    pl: 'Rozwój cyfrowy', ru: 'Цифровая разработка', zh: '数字开发', ja: 'デジタル開発',
    tr: 'Dijital Geliştirme', ko: '디지털 개발', sv: 'Digital utveckling', da: 'Digital udvikling',
  }, lang),

  saas: (lang: string) => t({
    en: 'SaaS Development', fr: 'Développement SaaS', es: 'Desarrollo SaaS', ar: 'تطوير SaaS',
    de: 'SaaS-Entwicklung', it: 'Sviluppo SaaS', pt: 'Desenvolvimento SaaS', nl: 'SaaS-ontwikkeling',
    pl: 'Rozwój SaaS', ru: 'Разработка SaaS', zh: 'SaaS开发', ja: 'SaaS開発',
    tr: 'SaaS Geliştirme', ko: 'SaaS 개발', sv: 'SaaS-utveckling', da: 'SaaS-udvikling',
  }, lang),

  aiAutomation: (lang: string) => t({
    en: 'AI & Automation', fr: 'IA & Automatisation', es: 'IA y Automatización', ar: 'الذكاء الاصطناعي والأتمتة',
    de: 'KI & Automatisierung', it: 'IA & Automazione', pt: 'IA & Automação', nl: 'AI & Automatisering',
    pl: 'AI i Automatyzacja', ru: 'ИИ и автоматизация', zh: 'AI与自动化', ja: 'AI＆自動化',
    tr: 'AI & Otomasyon', ko: 'AI & 자동화', sv: 'AI & Automatisering', da: 'AI & Automatisering',
  }, lang),

  aboutUs: (lang: string) => t({
    en: 'About Us', fr: 'À propos', es: 'Sobre nosotros', ar: 'من نحن',
    de: 'Über uns', it: 'Chi siamo', pt: 'Sobre nós', nl: 'Over ons',
    pl: 'O nas', ru: 'О нас', zh: '关于我们', ja: '会社概要',
    tr: 'Hakkımızda', ko: '회사 소개', sv: 'Om oss', da: 'Om os',
  }, lang),

  whyMorocco: (lang: string) => t({
    en: 'Why Morocco', fr: 'Pourquoi le Maroc', es: '¿Por qué Marruecos?', ar: 'لماذا المغرب',
    de: 'Warum Marokko', it: 'Perché il Marocco', pt: 'Por que Marrocos', nl: 'Waarom Marokko',
    pl: 'Dlaczego Maroko', ru: 'Почему Марокко', zh: '为什么选择摩洛哥', ja: 'なぜモロッコ',
    tr: 'Neden Fas', ko: '왜 모로코인가', sv: 'Varför Marocko', da: 'Hvorfor Marokko',
  }, lang),

  careers: (lang: string) => t({
    en: 'Careers', fr: 'Carrières', es: 'Carreras', ar: 'الوظائف',
    de: 'Karriere', it: 'Carriere', pt: 'Carreiras', nl: 'Vacatures',
    pl: 'Kariera', ru: 'Карьера', zh: '职业', ja: 'キャリア',
    tr: 'Kariyer', ko: '채용', sv: 'Karriär', da: 'Karriere',
  }, lang),

  moroccoHQ: (lang: string) => t({
    en: 'Morocco HQ', fr: 'Siège Maroc', es: 'Sede Marruecos', ar: 'المقر المغرب',
    de: 'Marokko HQ', it: 'Sede Marocco', pt: 'Sede Marrocos', nl: 'Marokko HQ',
    pl: 'Siedziba Maroko', ru: 'Штаб Марокко', zh: '摩洛哥总部', ja: 'モロッコ本社',
    tr: 'Fas Merkez', ko: '모로코 본사', sv: 'Marocko HK', da: 'Marokko HQ',
  }, lang),

  spainOffice: (lang: string) => t({
    en: 'Spain Office', fr: 'Bureau Espagne', es: 'Oficina España', ar: 'مكتب إسبانيا',
    de: 'Büro Spanien', it: 'Ufficio Spagna', pt: 'Escritório Espanha', nl: 'Kantoor Spanje',
    pl: 'Biuro Hiszpania', ru: 'Офис Испания', zh: '西班牙办事处', ja: 'スペインオフィス',
    tr: 'İspanya Ofisi', ko: '스페인 사무소', sv: 'Spanien-kontor', da: 'Spanien kontor',
  }, lang),

  dubaiOffice: (lang: string) => t({
    en: 'Dubai Office', fr: 'Bureau Dubaï', es: 'Oficina Dubái', ar: 'مكتب دبي',
    de: 'Büro Dubai', it: 'Ufficio Dubai', pt: 'Escritório Dubai', nl: 'Kantoor Dubai',
    pl: 'Biuro Dubaj', ru: 'Офис Дубай', zh: '迪拜办事处', ja: 'ドバイオフィス',
    tr: 'Dubai Ofisi', ko: '두바이 사무소', sv: 'Dubai-kontor', da: 'Dubai kontor',
  }, lang),

  partnersIntegrations: (lang: string) => t({
    en: 'Partners & Integrations', fr: 'Partenaires & Intégrations', es: 'Socios e Integraciones', ar: 'الشراكات والتكاملات',
    de: 'Partner & Integrationen', it: 'Partner e Integrazioni', pt: 'Parceiros & Integrações', nl: 'Partners & Integraties',
    pl: 'Partnerzy i Integracje', ru: 'Партнёры и интеграции', zh: '合作伙伴与集成', ja: 'パートナー＆連携',
    tr: 'Ortaklar & Entegrasyonlar', ko: '파트너 & 통합', sv: 'Partners & Integrationer', da: 'Partnere & Integrationer',
  }, lang),

  pressNews: (lang: string) => t({
    en: 'Press & News', fr: 'Presse & Actualités', es: 'Prensa y Noticias', ar: 'الصحافة والأخبار',
    de: 'Presse & News', it: 'Stampa & News', pt: 'Imprensa & Notícias', nl: 'Pers & Nieuws',
    pl: 'Prasa i Aktualności', ru: 'Пресса и Новости', zh: '新闻与报道', ja: 'プレス＆ニュース',
    tr: 'Basın & Haberler', ko: '보도자료 & 뉴스', sv: 'Press & Nyheter', da: 'Presse & Nyheder',
  }, lang),

  caseStudies: (lang: string) => t({
    en: 'Case Studies', fr: 'Études de cas', es: 'Casos de estudio', ar: 'دراسات الحالة',
    de: 'Fallstudien', it: 'Casi studio', pt: 'Estudos de caso', nl: 'Casestudies',
    pl: 'Studia przypadków', ru: 'Кейсы', zh: '案例研究', ja: 'ケーススタディ',
    tr: 'Vaka Çalışmaları', ko: '사례 연구', sv: 'Fallstudier', da: 'Casestudier',
  }, lang),

  blog: (lang: string) => t({
    en: 'Blog', fr: 'Blog', es: 'Blog', ar: 'مدونة',
    de: 'Blog', it: 'Blog', pt: 'Blog', nl: 'Blog',
    pl: 'Blog', ru: 'Блог', zh: '博客', ja: 'ブログ',
    tr: 'Blog', ko: '블로그', sv: 'Blogg', da: 'Blog',
  }, lang),

  faq: (lang: string) => t({
    en: 'FAQ', fr: 'FAQ', es: 'FAQ', ar: 'أسئلة شائعة',
    de: 'FAQ', it: 'FAQ', pt: 'FAQ', nl: 'FAQ',
    pl: 'FAQ', ru: 'ЧаВо', zh: '常见问题', ja: 'よくある質問',
    tr: 'SSS', ko: 'FAQ', sv: 'FAQ', da: 'FAQ',
  }, lang),

  bpoGlossary: (lang: string) => t({
    en: 'BPO Glossary', fr: 'Glossaire BPO', es: 'Glosario BPO', ar: 'مسرد BPO',
    de: 'BPO-Glossar', it: 'Glossario BPO', pt: 'Glossário BPO', nl: 'BPO-woordenlijst',
    pl: 'Słownik BPO', ru: 'Глоссарий BPO', zh: 'BPO术语表', ja: 'BPO用語集',
    tr: 'BPO Sözlüğü', ko: 'BPO 용어집', sv: 'BPO-ordlista', da: 'BPO-ordliste',
  }, lang),

  compareOptions: (lang: string) => t({
    en: 'Compare Options', fr: 'Comparer les options', es: 'Comparar opciones', ar: 'مقارنة الخيارات',
    de: 'Optionen vergleichen', it: 'Confronta opzioni', pt: 'Comparar opções', nl: 'Opties vergelijken',
    pl: 'Porównaj opcje', ru: 'Сравнить варианты', zh: '比较选项', ja: 'オプションを比較',
    tr: 'Seçenekleri Karşılaştır', ko: '옵션 비교', sv: 'Jämför alternativ', da: 'Sammenlign muligheder',
  }, lang),

  pricing: (lang: string) => t({
    en: 'Pricing', fr: 'Tarifs', es: 'Precios', ar: 'التسعير',
    de: 'Preise', it: 'Prezzi', pt: 'Preços', nl: 'Prijzen',
    pl: 'Cennik', ru: 'Цены', zh: '定价', ja: '料金',
    tr: 'Fiyatlandırma', ko: '가격', sv: 'Priser', da: 'Priser',
  }, lang),

  slaGuarantees: (lang: string) => t({
    en: 'SLA & Guarantees', fr: 'SLA & Garanties', es: 'SLA y Garantías', ar: 'SLA والضمانات',
    de: 'SLA & Garantien', it: 'SLA e Garanzie', pt: 'SLA & Garantias', nl: 'SLA & Garanties',
    pl: 'SLA i Gwarancje', ru: 'SLA и гарантии', zh: 'SLA与保证', ja: 'SLA＆保証',
    tr: 'SLA & Garantiler', ko: 'SLA & 보증', sv: 'SLA & Garantier', da: 'SLA & Garantier',
  }, lang),

  privacyPolicy: (lang: string) => t({
    en: 'Privacy Policy', fr: 'Politique de confidentialité', es: 'Política de privacidad', ar: 'سياسة الخصوصية',
    de: 'Datenschutzrichtlinie', it: 'Informativa sulla privacy', pt: 'Política de privacidade', nl: 'Privacybeleid',
    pl: 'Polityka prywatności', ru: 'Политика конфиденциальности', zh: '隐私政策', ja: 'プライバシーポリシー',
    tr: 'Gizlilik Politikası', ko: '개인정보 처리방침', sv: 'Integritetspolicy', da: 'Privatlivspolitik',
  }, lang),

  termsOfService: (lang: string) => t({
    en: 'Terms of Service', fr: 'Conditions d\'utilisation', es: 'Términos de servicio', ar: 'شروط الخدمة',
    de: 'Nutzungsbedingungen', it: 'Termini di servizio', pt: 'Termos de serviço', nl: 'Servicevoorwaarden',
    pl: 'Regulamin', ru: 'Условия использования', zh: '服务条款', ja: '利用規約',
    tr: 'Hizmet Şartları', ko: '서비스 약관', sv: 'Användarvillkor', da: 'Servicevilkår',
  }, lang),

  cookiePolicy: (lang: string) => t({
    en: 'Cookie Policy', fr: 'Politique des cookies', es: 'Política de cookies', ar: 'سياسة ملفات تعريف الارتباط',
    de: 'Cookie-Richtlinie', it: 'Politica sui cookie', pt: 'Política de cookies', nl: 'Cookiebeleid',
    pl: 'Polityka cookies', ru: 'Политика файлов cookie', zh: 'Cookie政策', ja: 'Cookieポリシー',
    tr: 'Çerez Politikası', ko: '쿠키 정책', sv: 'Cookiepolicy', da: 'Cookiepolitik',
  }, lang),

  accessibility: (lang: string) => t({
    en: 'Accessibility', fr: 'Accessibilité', es: 'Accesibilidad', ar: 'إمكانية الوصول',
    de: 'Barrierefreiheit', it: 'Accessibilità', pt: 'Acessibilidade', nl: 'Toegankelijkheid',
    pl: 'Dostępność', ru: 'Доступность', zh: '无障碍', ja: 'アクセシビリティ',
    tr: 'Erişilebilirlik', ko: '접근성', sv: 'Tillgänglighet', da: 'Tilgængelighed',
  }, lang),

  securityCompliance: (lang: string) => t({
    en: 'Security & Compliance', fr: 'Sécurité & Conformité', es: 'Seguridad y Cumplimiento', ar: 'الأمان والامتثال',
    de: 'Sicherheit & Compliance', it: 'Sicurezza e Conformità', pt: 'Segurança & Conformidade', nl: 'Beveiliging & Compliance',
    pl: 'Bezpieczeństwo i Zgodność', ru: 'Безопасность и соответствие', zh: '安全与合规', ja: 'セキュリティ＆コンプライアンス',
    tr: 'Güvenlik & Uyumluluk', ko: '보안 & 규정 준수', sv: 'Säkerhet & Efterlevnad', da: 'Sikkerhed & Compliance',
  }, lang),

  trustCenter: (lang: string) => t({
    en: 'Trust Center', fr: 'Centre de confiance', es: 'Centro de confianza', ar: 'مركز الثقة',
    de: 'Trust Center', it: 'Centro fiducia', pt: 'Central de confiança', nl: 'Vertrouwenscentrum',
    pl: 'Centrum zaufania', ru: 'Центр доверия', zh: '信任中心', ja: 'トラストセンター',
    tr: 'Güven Merkezi', ko: '신뢰 센터', sv: 'Förtroendecenter', da: 'Tillidscenter',
  }, lang),

  esgResponsibility: (lang: string) => t({
    en: 'ESG & Responsibility', fr: 'ESG & Responsabilité', es: 'ESG y Responsabilidad', ar: 'ESG والمسؤولية',
    de: 'ESG & Verantwortung', it: 'ESG e Responsabilità', pt: 'ESG & Responsabilidade', nl: 'ESG & Verantwoordelijkheid',
    pl: 'ESG i Odpowiedzialność', ru: 'ESG и ответственность', zh: 'ESG与责任', ja: 'ESG＆責任',
    tr: 'ESG & Sorumluluk', ko: 'ESG & 책임', sv: 'ESG & Ansvar', da: 'ESG & Ansvar',
  }, lang),

  whatsappChat: (lang: string) => t({
    en: 'WhatsApp Chat', fr: 'Chat WhatsApp', es: 'Chat WhatsApp', ar: 'محادثة واتساب',
    de: 'WhatsApp Chat', it: 'Chat WhatsApp', pt: 'Chat WhatsApp', nl: 'WhatsApp Chat',
    pl: 'Chat WhatsApp', ru: 'Чат WhatsApp', zh: 'WhatsApp聊天', ja: 'WhatsAppチャット',
    tr: 'WhatsApp Sohbet', ko: 'WhatsApp 채팅', sv: 'WhatsApp Chatt', da: 'WhatsApp Chat',
  }, lang),

  enterpriseSecurity: (lang: string) => t({
    en: 'Enterprise Security', fr: 'Sécurité entreprise', es: 'Seguridad empresarial', ar: 'أمن المؤسسات',
    de: 'Unternehmenssicherheit', it: 'Sicurezza enterprise', pt: 'Segurança empresarial', nl: 'Bedrijfsbeveiliging',
    pl: 'Bezpieczeństwo korporacyjne', ru: 'Корпоративная безопасность', zh: '企业安全', ja: 'エンタープライズセキュリティ',
    tr: 'Kurumsal Güvenlik', ko: '엔터프라이즈 보안', sv: 'Företagssäkerhet', da: 'Virksomhedssikkerhed',
  }, lang),

  gdprAligned: (lang: string) => t({
    en: 'GDPR-Aligned', fr: 'Conforme RGPD', es: 'Alineado con RGPD', ar: 'متوافق مع GDPR',
    de: 'DSGVO-konform', it: 'Conforme GDPR', pt: 'Alinhado ao RGPD', nl: 'AVG-conform',
    pl: 'Zgodny z RODO', ru: 'Соответствие GDPR', zh: '符合GDPR', ja: 'GDPR準拠',
    tr: 'GDPR Uyumlu', ko: 'GDPR 준수', sv: 'GDPR-anpassad', da: 'GDPR-tilpasset',
  }, lang),

  qualityBestPractices: (lang: string) => t({
    en: 'Quality Best Practices', fr: 'Meilleures pratiques qualité', es: 'Mejores prácticas de calidad', ar: 'أفضل ممارسات الجودة',
    de: 'Qualitäts-Best-Practices', it: 'Best practice di qualità', pt: 'Melhores práticas de qualidade', nl: 'Kwaliteitsstandaarden',
    pl: 'Najlepsze praktyki jakości', ru: 'Лучшие практики качества', zh: '质量最佳实践', ja: '品質ベストプラクティス',
    tr: 'Kalite En İyi Uygulamalar', ko: '품질 모범 사례', sv: 'Kvalitetsstandarder', da: 'Kvalitetsstandarder',
  }, lang),

  learnMore: (lang: string) => t({
    en: 'Learn more', fr: 'En savoir plus', es: 'Más información', ar: 'اعرف المزيد',
    de: 'Mehr erfahren', it: 'Scopri di più', pt: 'Saiba mais', nl: 'Meer informatie',
    pl: 'Dowiedz się więcej', ru: 'Подробнее', zh: '了解更多', ja: '詳しく見る',
    tr: 'Daha fazla bilgi', ko: '더 알아보기', sv: 'Läs mer', da: 'Læs mere',
  }, lang),

  exploreServices: (lang: string) => t({
    en: 'Explore Our Services', fr: 'Explorer nos services', es: 'Explorar nuestros servicios', ar: 'استكشف خدماتنا',
    de: 'Unsere Dienstleistungen entdecken', it: 'Esplora i nostri servizi', pt: 'Explorar nossos serviços', nl: 'Ontdek onze diensten',
    pl: 'Poznaj nasze usługi', ru: 'Изучите наши услуги', zh: '探索我们的服务', ja: 'サービスを探す',
    tr: 'Hizmetlerimizi keşfedin', ko: '서비스 둘러보기', sv: 'Utforska våra tjänster', da: 'Udforsk vores tjenester',
  }, lang),

  // === CTA & Sticky BPO Translations ===
  orderNow: (lang: string) => t({
    en: 'Order Now', fr: 'Commander', es: 'Pedir ahora', ar: 'اطلب الآن',
    de: 'Jetzt bestellen', it: 'Ordina ora', pt: 'Pedir agora', nl: 'Nu bestellen',
    pl: 'Zamów teraz', ru: 'Заказать сейчас', zh: '立即下单', ja: '今すぐ注文',
    tr: 'Şimdi Sipariş Ver', ko: '지금 주문', sv: 'Beställ nu', da: 'Bestil nu',
  }, lang),

  startFreePilotWeekShort: (lang: string) => t({
    en: 'Start Free Pilot Week', fr: 'Démarrer le Pilote Gratuit', es: 'Iniciar Piloto Gratis', ar: 'ابدأ أسبوع التجربة المجانية',
    de: 'Kostenlose Pilotwoche starten', it: 'Inizia Settimana Pilota', pt: 'Iniciar Semana Piloto', nl: 'Gratis Pilotweek Starten',
    pl: 'Rozpocznij Bezpłatny Pilotaż', ru: 'Начать Бесплатный Пилот', zh: '开始免费试点周', ja: '無料パイロット週間開始',
    tr: 'Ücretsiz Pilot Haftası Başlat', ko: '무료 파일럿 주간 시작', sv: 'Starta Gratis Pilotvecka', da: 'Start Gratis Pilotuge',
  }, lang),

  startFreePilot: (lang: string) => t({
    en: 'Start Free Pilot', fr: 'Démarrer l\'essai gratuit', es: 'Iniciar prueba gratis', ar: 'ابدأ التجربة المجانية',
    de: 'Kostenlose Testphase starten', it: 'Inizia prova gratuita', pt: 'Iniciar teste grátis', nl: 'Gratis proefperiode starten',
    pl: 'Rozpocznij bezpłatny pilotaż', ru: 'Начать бесплатный пилот', zh: '开始免费试用', ja: '無料パイロットを開始',
    tr: 'Ücretsiz Denemeyi Başlat', ko: '무료 파일럿 시작', sv: 'Starta gratis pilot', da: 'Start gratis pilotperiode',
  }, lang),

  startNow: (lang: string) => t({
    en: 'Start Now', fr: 'Commencer', es: 'Empezar ahora', ar: 'ابدأ الآن',
    de: 'Jetzt starten', it: 'Inizia ora', pt: 'Começar agora', nl: 'Nu starten',
    pl: 'Zacznij teraz', ru: 'Начать сейчас', zh: '立即开始', ja: '今すぐ開始',
    tr: 'Şimdi Başla', ko: '지금 시작', sv: 'Starta nu', da: 'Start nu',
  }, lang),

  startFreeWeekLong: (lang: string) => t({
    en: 'Start Your Free Week', fr: 'Démarrez votre semaine gratuite', es: 'Comience su semana gratis', ar: 'ابدأ أسبوعك المجاني',
    de: 'Starten Sie Ihre kostenlose Woche', it: 'Inizia la tua settimana gratuita', pt: 'Comece sua semana gratuita', nl: 'Start uw gratis week',
    pl: 'Rozpocznij darmowy tydzień', ru: 'Начните бесплатную неделю', zh: '开始免费体验周', ja: '無料ウィークを開始',
    tr: 'Ücretsiz Haftanızı Başlatın', ko: '무료 주간 시작', sv: 'Starta din gratis vecka', da: 'Start din gratis uge',
  }, lang),

  startFreePilotWeek: (lang: string) => t({
    en: 'Start your free pilot week', fr: 'Démarrez votre semaine pilote gratuite', es: 'Inicie su semana piloto gratis', ar: 'ابدأ أسبوعك التجريبي المجاني',
    de: 'Starten Sie Ihre kostenlose Pilotwoche', it: 'Inizia la settimana pilota gratuita', pt: 'Inicie sua semana piloto grátis', nl: 'Start uw gratis pilotweek',
    pl: 'Rozpocznij bezpłatny tydzień pilotażowy', ru: 'Начните бесплатную пилотную неделю', zh: '开始免费试点周', ja: '無料パイロット週間を開始',
    tr: 'Ücretsiz pilot haftanızı başlatın', ko: '무료 파일럿 주간 시작', sv: 'Starta din gratis pilotvecka', da: 'Start din gratis pilotuge',
  }, lang),

  deployIn7Days: (lang: string) => t({
    en: 'Deploy in 7 days. No commitment.', fr: 'Déploiement en 7 jours. Sans engagement.', es: 'Despliegue en 7 días. Sin compromiso.', ar: 'النشر في 7 أيام. بدون التزام.',
    de: 'Einsatz in 7 Tagen. Ohne Verpflichtung.', it: 'Attivazione in 7 giorni. Senza impegno.', pt: 'Implantação em 7 dias. Sem compromisso.', nl: 'Operationeel in 7 dagen. Geen verplichting.',
    pl: 'Wdrożenie w 7 dni. Bez zobowiązań.', ru: 'Запуск за 7 дней. Без обязательств.', zh: '7天部署。无需承诺。', ja: '7日で展開。義務なし。',
    tr: '7 günde devreye alın. Taahhüt yok.', ko: '7일 내 배포. 약정 없음.', sv: 'Driftsättning på 7 dagar. Inget åtagande.', da: 'Udrulning på 7 dage. Ingen forpligtelse.',
  }, lang),

  in7Days: (lang: string) => t({
    en: 'in 7 days', fr: 'en 7 jours', es: 'en 7 días', ar: 'في 7 أيام',
    de: 'in 7 Tagen', it: 'in 7 giorni', pt: 'em 7 dias', nl: 'in 7 dagen',
    pl: 'w 7 dni', ru: 'за 7 дней', zh: '7天内', ja: '7日で',
    tr: '7 günde', ko: '7일 내', sv: 'på 7 dagar', da: 'på 7 dage',
  }, lang),

  noCreditCard: (lang: string) => t({
    en: 'No credit card required', fr: 'Pas de carte de crédit requise', es: 'Sin tarjeta de crédito', ar: 'بدون بطاقة ائتمان',
    de: 'Keine Kreditkarte erforderlich', it: 'Nessuna carta di credito richiesta', pt: 'Sem cartão de crédito', nl: 'Geen creditcard nodig',
    pl: 'Bez karty kredytowej', ru: 'Без банковской карты', zh: '无需信用卡', ja: 'クレジットカード不要',
    tr: 'Kredi kartı gerekmez', ko: '신용카드 불필요', sv: 'Inget kreditkort krävs', da: 'Intet kreditkort påkrævet',
  }, lang),

  fullProductionPilot: (lang: string) => t({
    en: 'Full production pilot', fr: 'Pilote en production complète', es: 'Piloto en producción completa', ar: 'تجربة إنتاج كاملة',
    de: 'Vollständiger Produktionspilot', it: 'Pilota di produzione completo', pt: 'Piloto de produção completo', nl: 'Volledig productiepilot',
    pl: 'Pełny pilotaż produkcyjny', ru: 'Полный производственный пилот', zh: '全面生产试点', ja: '完全本番パイロット',
    tr: 'Tam üretim pilotu', ko: '전체 생산 파일럿', sv: 'Fullständig produktionspilot', da: 'Fuld produktionspilot',
  }, lang),

  qaReportingIncluded: (lang: string) => t({
    en: 'QA & reporting included', fr: 'QA et rapports inclus', es: 'QA e informes incluidos', ar: 'ضمان الجودة والتقارير مشمولة',
    de: 'QA & Berichte inklusive', it: 'QA e reportistica inclusi', pt: 'QA e relatórios incluídos', nl: 'QA en rapportage inbegrepen',
    pl: 'QA i raporty w cenie', ru: 'Контроль качества и отчёты включены', zh: '含质量保证和报告', ja: 'QAとレポート込み',
    tr: 'Kalite kontrol ve raporlama dahil', ko: 'QA 및 보고 포함', sv: 'QA och rapportering ingår', da: 'QA og rapportering inkluderet',
  }, lang),

  scheduleDemo: (lang: string) => t({
    en: 'Schedule Demo', fr: 'Planifier une démo', es: 'Programar demo', ar: 'جدولة عرض تجريبي',
    de: 'Demo vereinbaren', it: 'Prenota demo', pt: 'Agendar demonstração', nl: 'Demo plannen',
    pl: 'Zaplanuj demo', ru: 'Запланировать демо', zh: '预约演示', ja: 'デモを予約',
    tr: 'Demo Planla', ko: '데모 예약', sv: 'Boka demo', da: 'Planlæg demo',
  }, lang),

  readyToTransformOps: (lang: string) => t({
    en: 'Ready to Transform Your Operations?', fr: 'Prêt à transformer vos opérations ?', es: '¿Listo para transformar sus operaciones?', ar: 'هل أنت مستعد لتحويل عملياتك؟',
    de: 'Bereit, Ihre Abläufe zu transformieren?', it: 'Pronti a trasformare le vostre operazioni?', pt: 'Pronto para transformar suas operações?', nl: 'Klaar om uw bedrijfsprocessen te transformeren?',
    pl: 'Gotowy na transformację swoich operacji?', ru: 'Готовы трансформировать ваши операции?', zh: '准备好转变您的运营了吗？', ja: 'オペレーション変革の準備はできていますか？',
    tr: 'Operasyonlarınızı dönüştürmeye hazır mısınız?', ko: '운영을 혁신할 준비가 되셨나요?', sv: 'Redo att transformera era verksamheter?', da: 'Klar til at transformere jeres drift?',
  }, lang),

  riskFreePilot: (lang: string) => t({
    en: 'Start with a risk-free pilot week. No credit card required. Deploy in 7 days.', fr: 'Commencez avec une semaine pilote sans risque. Pas de carte de crédit. Déploiement en 7 jours.', es: 'Comience con una semana piloto sin riesgo. Sin tarjeta de crédito. Despliegue en 7 días.', ar: 'ابدأ بأسبوع تجريبي بدون مخاطر. بدون بطاقة ائتمان. نشر في 7 أيام.',
    de: 'Starten Sie mit einer risikofreien Pilotwoche. Keine Kreditkarte nötig. Einsatz in 7 Tagen.', it: 'Inizia con una settimana pilota senza rischi. Nessuna carta di credito. Attivazione in 7 giorni.', pt: 'Comece com uma semana piloto sem riscos. Sem cartão de crédito. Implantação em 7 dias.', nl: 'Start met een risicovrije pilotweek. Geen creditcard nodig. Operationeel in 7 dagen.',
    pl: 'Rozpocznij bezpłatny tydzień pilotażowy. Bez karty kredytowej. Wdrożenie w 7 dni.', ru: 'Начните с бесплатной пилотной недели. Без банковской карты. Запуск за 7 дней.', zh: '从无风险试点周开始。无需信用卡。7天部署。', ja: 'リスクなしのパイロット週間から始めましょう。クレジットカード不要。7日で展開。',
    tr: 'Risksiz bir pilot hafta ile başlayın. Kredi kartı gerekmez. 7 günde devreye alın.', ko: '위험 없는 파일럿 주간으로 시작하세요. 신용카드 불필요. 7일 내 배포.', sv: 'Börja med en riskfri pilotvecka. Inget kreditkort krävs. Driftsättning på 7 dagar.', da: 'Start med en risikofri pilotuge. Intet kreditkort påkrævet. Udrulning på 7 dage.',
  }, lang),

  firstWeekFree: (lang: string) => t({
    en: '🎁 First Week Free — Zero Risk', fr: '🎁 Première semaine gratuite — Zéro risque', es: '🎁 Primera semana gratis — Sin riesgo', ar: '🎁 الأسبوع الأول مجاني — بدون مخاطر',
    de: '🎁 Erste Woche kostenlos — Kein Risiko', it: '🎁 Prima settimana gratuita — Zero rischi', pt: '🎁 Primeira semana grátis — Sem risco', nl: '🎁 Eerste week gratis — Geen risico',
    pl: '🎁 Pierwszy tydzień gratis — Zero ryzyka', ru: '🎁 Первая неделя бесплатно — Без рисков', zh: '🎁 首周免费 — 零风险', ja: '🎁 初週無料 — リスクゼロ',
    tr: '🎁 İlk Hafta Ücretsiz — Sıfır Risk', ko: '🎁 첫 주 무료 — 위험 제로', sv: '🎁 Första veckan gratis — Ingen risk', da: '🎁 Første uge gratis — Ingen risiko',
  }, lang),

  firstWeekFreeNoCommitment: (lang: string) => t({
    en: '🎁 First Week Free — No Commitment', fr: '🎁 Première semaine gratuite — Sans engagement', es: '🎁 Primera semana gratis — Sin compromiso', ar: '🎁 الأسبوع الأول مجاني — بدون التزام',
    de: '🎁 Erste Woche kostenlos — Ohne Verpflichtung', it: '🎁 Prima settimana gratuita — Senza impegno', pt: '🎁 Primeira semana grátis — Sem compromisso', nl: '🎁 Eerste week gratis — Zonder verplichting',
    pl: '🎁 Pierwszy tydzień gratis — Bez zobowiązań', ru: '🎁 Первая неделя бесплатно — Без обязательств', zh: '🎁 首周免费 — 无需承诺', ja: '🎁 初週無料 — 義務なし',
    tr: '🎁 İlk Hafta Ücretsiz — Taahhüt Yok', ko: '🎁 첫 주 무료 — 약정 없음', sv: '🎁 Första veckan gratis — Inget åtagande', da: '🎁 Første uge gratis — Ingen forpligtelse',
  }, lang),

  cancelAnytime: (lang: string) => t({
    en: 'Cancel anytime', fr: 'Annulez à tout moment', es: 'Cancele en cualquier momento', ar: 'إلغاء في أي وقت',
    de: 'Jederzeit kündbar', it: 'Cancella in qualsiasi momento', pt: 'Cancele a qualquer momento', nl: 'Altijd opzegbaar',
    pl: 'Anuluj w dowolnym momencie', ru: 'Отмена в любой момент', zh: '随时取消', ja: 'いつでもキャンセル可能',
    tr: 'İstediğiniz zaman iptal edin', ko: '언제든 취소 가능', sv: 'Avsluta när som helst', da: 'Opsig når som helst',
  }, lang),

  deployment7Days: (lang: string) => t({
    en: '7-day deployment', fr: 'Déploiement en 7 jours', es: 'Despliegue en 7 días', ar: 'نشر في 7 أيام',
    de: '7-Tage-Einsatz', it: 'Attivazione in 7 giorni', pt: 'Implantação em 7 dias', nl: 'Operationeel in 7 dagen',
    pl: 'Wdrożenie w 7 dni', ru: 'Запуск за 7 дней', zh: '7天部署', ja: '7日で展開',
    tr: '7 gün içinde devreye alma', ko: '7일 배포', sv: 'Driftsättning på 7 dagar', da: 'Udrulning på 7 dage',
  }, lang),

  csatGuarantee: (lang: string) => t({
    en: '95%+ CSAT guarantee', fr: 'Garantie CSAT 95%+', es: 'Garantía CSAT 95%+', ar: 'ضمان CSAT 95%+',
    de: '95%+ CSAT-Garantie', it: 'Garanzia CSAT 95%+', pt: 'Garantia CSAT 95%+', nl: '95%+ CSAT-garantie',
    pl: 'Gwarancja CSAT 95%+', ru: 'Гарантия CSAT 95%+', zh: 'CSAT 95%+ 保证', ja: 'CSAT 95%+保証',
    tr: '%95+ CSAT garantisi', ko: 'CSAT 95%+ 보장', sv: '95%+ CSAT-garanti', da: '95%+ CSAT-garanti',
  }, lang),

  fromPerHour: (lang: string) => t({
    en: 'From €3/hr', fr: 'À partir de 3€/h', es: 'Desde €3/h', ar: 'من 3€/ساعة',
    de: 'Ab 3€/Std.', it: 'Da 3€/h', pt: 'A partir de €3/h', nl: 'Vanaf €3/uur',
    pl: 'Od 3€/godz.', ru: 'От €3/час', zh: '低至€3/小时', ja: '€3/時間から',
    tr: 'Saatte €3\'dan başlayan', ko: '시간당 €3부터', sv: 'Från €3/tim', da: 'Fra €3/time',
  }, lang),

  // === FinalCTASection ===
  readyToTransformSupport: (lang: string) => t({
    en: 'Ready to Transform Your Customer Support?', fr: 'Prêt à transformer votre service client ?', es: '¿Listo para transformar su atención al cliente?', ar: 'هل أنت مستعد لتحويل دعم العملاء؟',
    de: 'Bereit, Ihren Kundensupport zu transformieren?', it: 'Pronti a trasformare il vostro supporto clienti?', pt: 'Pronto para transformar seu suporte ao cliente?', nl: 'Klaar om uw klantenservice te transformeren?',
    pl: 'Gotowy na transformację obsługi klienta?', ru: 'Готовы трансформировать клиентскую поддержку?', zh: '准备好转变您的客户支持了吗？', ja: 'カスタマーサポートを変革する準備はできていますか？',
    tr: 'Müşteri Desteğinizi Dönüştürmeye Hazır mısınız?', ko: '고객 지원을 혁신할 준비가 되셨나요?', sv: 'Redo att förvandla er kundsupport?', da: 'Klar til at transformere din kundesupport?',
  }, lang),

  riskFreePilotFull: (lang: string) => t({
    en: 'Start with a risk-free pilot week. No credit card required. Deploy in 7 days with real agents, real tickets, real results.', fr: 'Commencez par une semaine pilote sans risque. Pas de carte de crédit. Déploiement en 7 jours avec de vrais agents, de vrais tickets, de vrais résultats.', es: 'Comience con una semana piloto sin riesgo. Sin tarjeta de crédito. Despliegue en 7 días con agentes reales, tickets reales, resultados reales.', ar: 'ابدأ بأسبوع تجريبي بدون مخاطر. بدون بطاقة ائتمان. نشر في 7 أيام مع وكلاء حقيقيين وتذاكر حقيقية ونتائج حقيقية.',
    de: 'Starten Sie mit einer risikofreien Pilotwoche. Keine Kreditkarte nötig. Einsatz in 7 Tagen mit echten Agenten, echten Tickets, echten Ergebnissen.', it: 'Inizia con una settimana pilota senza rischi. Nessuna carta di credito. Attivazione in 7 giorni con agenti veri, ticket veri, risultati veri.', pt: 'Comece com uma semana piloto sem riscos. Sem cartão de crédito. Implantação em 7 dias com agentes reais, tickets reais, resultados reais.', nl: 'Start met een risicovrije pilotweek. Geen creditcard nodig. Operationeel in 7 dagen met echte agenten, echte tickets, echte resultaten.',
    pl: 'Rozpocznij bezpłatny tydzień pilotażowy. Bez karty kredytowej. Wdrożenie w 7 dni z prawdziwymi agentami, prawdziwymi zgłoszeniami, prawdziwymi wynikami.', ru: 'Начните с бесплатной пилотной недели. Без банковской карты. Запуск за 7 дней с реальными агентами, реальными тикетами, реальными результатами.', zh: '从无风险试点周开始。无需信用卡。7天部署，真实坐席、真实工单、真实成果。', ja: 'リスクなしのパイロット週間から。クレジットカード不要。7日で展開、本物のエージェント、本物のチケット、本物の成果。',
    tr: 'Risksiz pilot hafta ile başlayın. Kredi kartı gerekmez. 7 günde gerçek ajanlar, gerçek biletler, gerçek sonuçlar.', ko: '위험 없는 파일럿 주간으로 시작하세요. 신용카드 불필요. 7일 내 실제 상담원, 실제 티켓, 실제 결과.', sv: 'Börja med en riskfri pilotvecka. Inget kreditkort krävs. Driftsättning på 7 dagar med riktiga agenter, riktiga ärenden, riktiga resultat.', da: 'Start med en risikofri pilotuge. Intet kreditkort påkrævet. Udrulning på 7 dage med rigtige agenter, rigtige sager, rigtige resultater.',
  }, lang),

  startFreeWeekCTA: (lang: string) => t({
    en: 'Start Free Week', fr: 'Démarrer la semaine gratuite', es: 'Iniciar semana gratis', ar: 'ابدأ الأسبوع المجاني',
    de: 'Kostenlose Woche starten', it: 'Inizia settimana gratuita', pt: 'Iniciar semana grátis', nl: 'Gratis week starten',
    pl: 'Rozpocznij darmowy tydzień', ru: 'Начать бесплатную неделю', zh: '开始免费周', ja: '無料週間を開始',
    tr: 'Ücretsiz Haftayı Başlat', ko: '무료 주간 시작', sv: 'Starta gratis vecka', da: 'Start gratis uge',
  }, lang),

  bookADemo: (lang: string) => t({
    en: 'Book a Demo', fr: 'Réserver une démo', es: 'Reservar demo', ar: 'احجز عرضاً',
    de: 'Demo buchen', it: 'Prenota una demo', pt: 'Agendar demo', nl: 'Demo boeken',
    pl: 'Zarezerwuj demo', ru: 'Забронировать демо', zh: '预约演示', ja: 'デモを予約',
    tr: 'Demo Rezerve Et', ko: '데모 예약', sv: 'Boka demo', da: 'Book demo',
  }, lang),

  freePilotWeekBadge: (lang: string) => t({
    en: '🎁 Free Pilot Week', fr: '🎁 Semaine pilote gratuite', es: '🎁 Semana piloto gratis', ar: '🎁 أسبوع تجريبي مجاني',
    de: '🎁 Kostenlose Pilotwoche', it: '🎁 Settimana pilota gratuita', pt: '🎁 Semana piloto grátis', nl: '🎁 Gratis pilotweek',
    pl: '🎁 Darmowy tydzień pilotażowy', ru: '🎁 Бесплатная пилотная неделя', zh: '🎁 免费试点周', ja: '🎁 無料パイロット週間',
    tr: '🎁 Ücretsiz Pilot Hafta', ko: '🎁 무료 파일럿 주간', sv: '🎁 Gratis pilotvecka', da: '🎁 Gratis pilotuge',
  }, lang),

  fixedPricePackagesBadge: (lang: string) => t({
    en: '💼 Fixed-Price Packages', fr: '💼 Forfaits à prix fixe', es: '💼 Paquetes de precio fijo', ar: '💼 باقات بسعر ثابت',
    de: '💼 Festpreispakete', it: '💼 Pacchetti a prezzo fisso', pt: '💼 Pacotes de preço fixo', nl: '💼 Vaste-prijspakketten',
    pl: '💼 Pakiety w stałej cenie', ru: '💼 Пакеты с фиксированной ценой', zh: '💼 固定价格套餐', ja: '💼 固定価格パッケージ',
    tr: '💼 Sabit Fiyatlı Paketler', ko: '💼 정가 패키지', sv: '💼 Fastprispaket', da: '💼 Fastprispakker',
  }, lang),

  readyToLaunchDigital: (lang: string) => t({
    en: 'Ready to Launch Your Digital Product?', fr: 'Prêt à lancer votre produit digital ?', es: '¿Listo para lanzar su producto digital?', ar: 'هل أنت مستعد لإطلاق منتجك الرقمي؟',
    de: 'Bereit, Ihr digitales Produkt zu starten?', it: 'Pronti a lanciare il vostro prodotto digitale?', pt: 'Pronto para lançar seu produto digital?', nl: 'Klaar om uw digitale product te lanceren?',
    pl: 'Gotowy do uruchomienia produktu cyfrowego?', ru: 'Готовы запустить ваш цифровой продукт?', zh: '准备好推出您的数字产品了吗？', ja: 'デジタル製品をローンチする準備はできていますか？',
    tr: 'Dijital Ürününüzü Başlatmaya Hazır mısınız?', ko: '디지털 제품을 출시할 준비가 되셨나요?', sv: 'Redo att lansera er digitala produkt?', da: 'Klar til at lancere dit digitale produkt?',
  }, lang),

  fixedPriceQuoteDesc: (lang: string) => t({
    en: 'Get a fixed-price quote for your web app, mobile app, or SaaS platform. Enterprise-quality at 65% below agency rates.', fr: 'Obtenez un devis à prix fixe pour votre application web, mobile ou plateforme SaaS. Qualité entreprise à 65% sous les tarifs d\'agence.', es: 'Obtenga un presupuesto de precio fijo para su aplicación web, móvil o plataforma SaaS. Calidad empresarial a un 65% por debajo de las tarifas de agencia.', ar: 'احصل على عرض سعر ثابت لتطبيق الويب أو الهاتف المحمول أو منصة SaaS. جودة مؤسسية بتكلفة أقل بنسبة 65% من أسعار الوكالات.',
    de: 'Erhalten Sie ein Festpreisangebot für Ihre Web-App, Mobile App oder SaaS-Plattform. Enterprise-Qualität zu 65% unter Agenturpreisen.', it: 'Ottieni un preventivo a prezzo fisso per la tua web app, app mobile o piattaforma SaaS. Qualità enterprise al 65% in meno delle tariffe delle agenzie.', pt: 'Receba um orçamento de preço fixo para seu aplicativo web, mobile ou plataforma SaaS. Qualidade empresarial a 65% abaixo dos preços de agências.', nl: 'Ontvang een offerte met vaste prijs voor uw web-app, mobiele app of SaaS-platform. Enterprise-kwaliteit tegen 65% onder bureautarieven.',
    pl: 'Uzyskaj wycenę w stałej cenie dla Twojej aplikacji webowej, mobilnej lub platformy SaaS. Jakość korporacyjna 65% poniżej stawek agencji.', ru: 'Получите фиксированную цену на веб-приложение, мобильное приложение или SaaS-платформу. Корпоративное качество на 65% ниже агентских цен.', zh: '获取固定价格报价，适用于您的Web应用、移动应用或SaaS平台。企业级质量，低于代理机构费用65%。', ja: 'Webアプリ、モバイルアプリ、SaaSプラットフォームの固定価格見積もりを取得。エージェンシー料金より65%安い企業品質。',
    tr: 'Web uygulamanız, mobil uygulamanız veya SaaS platformunuz için sabit fiyat teklifi alın. Ajans fiyatlarının %65 altında kurumsal kalite.', ko: '웹 앱, 모바일 앱 또는 SaaS 플랫폼에 대한 정가 견적을 받으세요. 에이전시 요금보다 65% 저렴한 엔터프라이즈 품질.', sv: 'Få en offert med fast pris för din webbapp, mobilapp eller SaaS-plattform. Företagskvalitet till 65% under byrå-priser.', da: 'Få et tilbud med fast pris på din webapp, mobilapp eller SaaS-platform. Enterprise-kvalitet til 65% under bureau-priser.',
  }, lang),

  getProjectQuote: (lang: string) => t({
    en: 'Get Project Quote', fr: 'Obtenir un devis', es: 'Obtener presupuesto', ar: 'احصل على عرض سعر',
    de: 'Projektangebot einholen', it: 'Richiedi preventivo', pt: 'Solicitar orçamento', nl: 'Offerte aanvragen',
    pl: 'Uzyskaj wycenę projektu', ru: 'Получить стоимость проекта', zh: '获取项目报价', ja: 'プロジェクト見積もりを取得',
    tr: 'Proje Teklifi Al', ko: '프로젝트 견적 받기', sv: 'Begär projektoffert', da: 'Få projekttilbud',
  }, lang),

  discussYourProject: (lang: string) => t({
    en: 'Discuss Your Project', fr: 'Discuter de votre projet', es: 'Discutir su proyecto', ar: 'ناقش مشروعك',
    de: 'Ihr Projekt besprechen', it: 'Discuti il tuo progetto', pt: 'Discutir seu projeto', nl: 'Uw project bespreken',
    pl: 'Omów swój projekt', ru: 'Обсудить ваш проект', zh: '讨论您的项目', ja: 'プロジェクトを相談',
    tr: 'Projenizi Tartışın', ko: '프로젝트 상담', sv: 'Diskutera ert projekt', da: 'Diskuter dit projekt',
  }, lang),

  noCreditCardRequired: (lang: string) => t({
    en: 'No credit card required', fr: 'Pas de carte de crédit', es: 'Sin tarjeta de crédito', ar: 'بدون بطاقة ائتمان',
    de: 'Keine Kreditkarte nötig', it: 'Nessuna carta di credito', pt: 'Sem cartão de crédito', nl: 'Geen creditcard nodig',
    pl: 'Bez karty kredytowej', ru: 'Без банковской карты', zh: '无需信用卡', ja: 'クレジットカード不要',
    tr: 'Kredi kartı gerekmez', ko: '신용카드 불필요', sv: 'Inget kreditkort krävs', da: 'Intet kreditkort påkrævet',
  }, lang),

  fixedPricePackages: (lang: string) => t({
    en: 'Fixed-price packages', fr: 'Forfaits à prix fixe', es: 'Paquetes de precio fijo', ar: 'باقات بسعر ثابت',
    de: 'Festpreispakete', it: 'Pacchetti a prezzo fisso', pt: 'Pacotes de preço fixo', nl: 'Vaste-prijspakketten',
    pl: 'Pakiety w stałej cenie', ru: 'Пакеты с фиксированной ценой', zh: '固定价格套餐', ja: '固定価格パッケージ',
    tr: 'Sabit fiyatlı paketler', ko: '정가 패키지', sv: 'Fastprispaket', da: 'Fastprispakker',
  }, lang),

  mvpDelivery: (lang: string) => t({
    en: '8-12 week MVP delivery', fr: 'MVP livré en 8-12 semaines', es: 'Entrega MVP en 8-12 semanas', ar: 'تسليم MVP في 8-12 أسبوع',
    de: '8-12 Wochen MVP-Lieferung', it: 'Consegna MVP in 8-12 settimane', pt: 'Entrega MVP em 8-12 semanas', nl: '8-12 weken MVP-levering',
    pl: 'MVP w 8-12 tygodni', ru: 'MVP за 8-12 недель', zh: '8-12周MVP交付', ja: '8-12週間のMVP納品',
    tr: '8-12 hafta MVP teslimatı', ko: '8-12주 MVP 제공', sv: '8-12 veckors MVP-leverans', da: '8-12 ugers MVP-levering',
  }, lang),

  postLaunchSupport: (lang: string) => t({
    en: 'Post-launch support', fr: 'Support post-lancement', es: 'Soporte post-lanzamiento', ar: 'دعم ما بعد الإطلاق',
    de: 'Support nach dem Launch', it: 'Supporto post-lancio', pt: 'Suporte pós-lançamento', nl: 'Ondersteuning na lancering',
    pl: 'Wsparcie po uruchomieniu', ru: 'Поддержка после запуска', zh: '上线后支持', ja: 'リリース後サポート',
    tr: 'Lansman sonrası destek', ko: '출시 후 지원', sv: 'Support efter lansering', da: 'Support efter lancering',
  }, lang),

  soc2Compliant: (lang: string) => t({
    en: 'SOC 2 compliant architecture', fr: 'Architecture conforme SOC 2', es: 'Arquitectura compatible SOC 2', ar: 'بنية متوافقة مع SOC 2',
    de: 'SOC 2 konforme Architektur', it: 'Architettura conforme SOC 2', pt: 'Arquitetura compatível com SOC 2', nl: 'SOC 2 conforme architectuur',
    pl: 'Architektura zgodna z SOC 2', ru: 'Архитектура SOC 2', zh: 'SOC 2合规架构', ja: 'SOC 2準拠アーキテクチャ',
    tr: 'SOC 2 uyumlu mimari', ko: 'SOC 2 호환 아키텍처', sv: 'SOC 2-kompatibel arkitektur', da: 'SOC 2 kompatibel arkitektur',
  }, lang),

  // === DeliveryTeamSection ===
  dedicatedResources: (lang: string) => t({
    en: 'Dedicated Resources', fr: 'Ressources dédiées', es: 'Recursos dedicados', ar: 'موارد مخصصة',
    de: 'Dedizierte Ressourcen', it: 'Risorse dedicate', pt: 'Recursos dedicados', nl: 'Toegewijde middelen',
    pl: 'Dedykowane zasoby', ru: 'Выделенные ресурсы', zh: '专属资源', ja: '専任リソース',
    tr: 'Ayrılmış Kaynaklar', ko: '전담 리소스', sv: 'Dedikerade resurser', da: 'Dedikerede ressourcer',
  }, lang),

  yourDeliveryTeam: (lang: string) => t({
    en: 'Your Delivery Team', fr: 'Votre équipe de livraison', es: 'Su equipo de entrega', ar: 'فريق التسليم الخاص بك',
    de: 'Ihr Delivery-Team', it: 'Il vostro team di delivery', pt: 'Sua equipe de entrega', nl: 'Uw leveringsteam',
    pl: 'Twój zespół realizacyjny', ru: 'Ваша команда доставки', zh: '您的交付团队', ja: 'お客様のデリバリーチーム',
    tr: 'Teslimat Ekibiniz', ko: '귀하의 전담 팀', sv: 'Ert leveransteam', da: 'Jeres leveringsteam',
  }, lang),

  deliveryTeamDesc: (lang: string) => t({
    en: 'Every engagement includes a dedicated team of specialists to ensure operational excellence and continuous improvement.', fr: 'Chaque engagement comprend une équipe dédiée de spécialistes pour garantir l\'excellence opérationnelle et l\'amélioration continue.', es: 'Cada compromiso incluye un equipo dedicado de especialistas para garantizar la excelencia operativa y la mejora continua.', ar: 'كل مشاركة تتضمن فريقاً مخصصاً من المتخصصين لضمان التميز التشغيلي والتحسين المستمر.',
    de: 'Jedes Engagement umfasst ein dediziertes Team von Spezialisten für operationelle Exzellenz und kontinuierliche Verbesserung.', it: 'Ogni incarico include un team dedicato di specialisti per garantire eccellenza operativa e miglioramento continuo.', pt: 'Cada engajamento inclui uma equipe dedicada de especialistas para garantir excelência operacional e melhoria contínua.', nl: 'Elk project omvat een toegewijd team van specialisten voor operationele uitmuntendheid en continue verbetering.',
    pl: 'Każde zaangażowanie obejmuje dedykowany zespół specjalistów zapewniający doskonałość operacyjną i ciągłe doskonalenie.', ru: 'Каждый проект включает выделенную команду специалистов для обеспечения операционного совершенства и непрерывного улучшения.', zh: '每次合作都配备专属专家团队，确保卓越运营和持续改进。', ja: 'すべてのエンゲージメントに、オペレーショナルエクセレンスと継続的改善を確保する専任チームが含まれます。',
    tr: 'Her proje, operasyonel mükemmellik ve sürekli iyileştirme sağlamak için uzman bir ekip içerir.', ko: '모든 프로젝트에는 운영 우수성과 지속적 개선을 위한 전담 전문가 팀이 포함됩니다.', sv: 'Varje uppdrag inkluderar ett dedikerat team av specialister för operativ excellens och kontinuerlig förbättring.', da: 'Hvert engagement inkluderer et dedikeret team af specialister for operationel ekspertise og løbende forbedring.',
  }, lang),

  // === OutcomesKPISection ===
  measurableOutcomes: (lang: string) => t({
    en: 'Measurable Outcomes', fr: 'Résultats mesurables', es: 'Resultados medibles', ar: 'نتائج قابلة للقياس',
    de: 'Messbare Ergebnisse', it: 'Risultati misurabili', pt: 'Resultados mensuráveis', nl: 'Meetbare resultaten',
    pl: 'Mierzalne wyniki', ru: 'Измеримые результаты', zh: '可衡量的成果', ja: '測定可能な成果',
    tr: 'Ölçülebilir Sonuçlar', ko: '측정 가능한 성과', sv: 'Mätbara resultat', da: 'Målbare resultater',
  }, lang),

  outcomesAndKPIs: (lang: string) => t({
    en: 'Outcomes & KPIs', fr: 'Résultats et KPIs', es: 'Resultados y KPIs', ar: 'النتائج ومؤشرات الأداء',
    de: 'Ergebnisse & KPIs', it: 'Risultati e KPI', pt: 'Resultados e KPIs', nl: 'Resultaten & KPI\'s',
    pl: 'Wyniki i KPI', ru: 'Результаты и KPI', zh: '成果与KPI', ja: '成果とKPI',
    tr: 'Sonuçlar ve KPI\'lar', ko: '성과 및 KPI', sv: 'Resultat & KPI:er', da: 'Resultater & KPI\'er',
  }, lang),

  outcomesKPIDesc: (lang: string) => t({
    en: 'We commit to clear, measurable performance targets. Every metric is tracked in real-time and visible in your Client Portal.', fr: 'Nous nous engageons sur des objectifs de performance clairs et mesurables. Chaque métrique est suivie en temps réel et visible dans votre Portail Client.', es: 'Nos comprometemos con objetivos de rendimiento claros y medibles. Cada métrica se rastrea en tiempo real y es visible en su Portal de Cliente.', ar: 'نلتزم بأهداف أداء واضحة وقابلة للقياس. يتم تتبع كل مقياس في الوقت الفعلي وعرضه في بوابة العملاء.',
    de: 'Wir verpflichten uns zu klaren, messbaren Leistungszielen. Jede Metrik wird in Echtzeit verfolgt und in Ihrem Kundenportal angezeigt.', it: 'Ci impegniamo per obiettivi di performance chiari e misurabili. Ogni metrica viene tracciata in tempo reale e visibile nel vostro Portale Cliente.', pt: 'Comprometemo-nos com metas de desempenho claras e mensuráveis. Cada métrica é rastreada em tempo real e visível no seu Portal do Cliente.', nl: 'Wij committeren ons aan duidelijke, meetbare prestatiedoelen. Elke metric wordt in real-time gevolgd en is zichtbaar in uw Klantenportaal.',
    pl: 'Zobowiązujemy się do jasnych, mierzalnych celów wydajnościowych. Każda metryka jest śledzona w czasie rzeczywistym i widoczna w Twoim Portalu Klienta.', ru: 'Мы берём на себя чёткие, измеримые цели по производительности. Каждая метрика отслеживается в реальном времени и видна на вашем Клиентском портале.', zh: '我们承诺明确、可衡量的绩效目标。每项指标实时跟踪，并在您的客户门户中可见。', ja: '明確で測定可能なパフォーマンス目標にコミットします。すべての指標はリアルタイムで追跡され、クライアントポータルで確認できます。',
    tr: 'Net, ölçülebilir performans hedeflerine bağlıyız. Her metrik gerçek zamanlı olarak izlenir ve Müşteri Portalınızda görüntülenebilir.', ko: '명확하고 측정 가능한 성과 목표에 전념합니다. 모든 지표는 실시간으로 추적되며 고객 포털에서 확인할 수 있습니다.', sv: 'Vi förbinder oss till tydliga, mätbara prestationsmål. Varje mätetal följs i realtid och syns i er Kundportal.', da: 'Vi forpligter os til klare, målbare præstationsmål. Alle metrikker spores i realtid og er synlige i din Kundeportal.',
  }, lang),

  targetRange: (lang: string) => t({
    en: 'Target Range', fr: 'Fourchette cible', es: 'Rango objetivo', ar: 'النطاق المستهدف',
    de: 'Zielbereich', it: 'Intervallo obiettivo', pt: 'Faixa alvo', nl: 'Doelbereik',
    pl: 'Zakres docelowy', ru: 'Целевой диапазон', zh: '目标范围', ja: 'ターゲット範囲',
    tr: 'Hedef Aralık', ko: '목표 범위', sv: 'Målintervall', da: 'Målområde',
  }, lang),

  allKPIsTracked: (lang: string) => t({
    en: 'All KPIs are tracked in real-time and reported weekly in your Client Portal', fr: 'Tous les KPIs sont suivis en temps réel et rapportés chaque semaine dans votre Portail Client', es: 'Todos los KPIs se rastrean en tiempo real y se informan semanalmente en su Portal de Cliente', ar: 'يتم تتبع جميع مؤشرات الأداء في الوقت الفعلي والإبلاغ عنها أسبوعياً في بوابة العملاء',
    de: 'Alle KPIs werden in Echtzeit verfolgt und wöchentlich in Ihrem Kundenportal berichtet', it: 'Tutti i KPI vengono tracciati in tempo reale e riportati settimanalmente nel vostro Portale Cliente', pt: 'Todos os KPIs são rastreados em tempo real e reportados semanalmente no seu Portal do Cliente', nl: 'Alle KPI\'s worden in real-time gevolgd en wekelijks gerapporteerd in uw Klantenportaal',
    pl: 'Wszystkie KPI są śledzone w czasie rzeczywistym i raportowane co tydzień w Twoim Portalu Klienta', ru: 'Все KPI отслеживаются в реальном времени и еженедельно отчитываются на вашем Клиентском портале', zh: '所有KPI实时跟踪，每周在您的客户门户中报告', ja: 'すべてのKPIはリアルタイムで追跡され、クライアントポータルで毎週報告されます',
    tr: 'Tüm KPI\'lar gerçek zamanlı izlenir ve Müşteri Portalınızda haftalık olarak raporlanır', ko: '모든 KPI는 실시간으로 추적되며 고객 포털에서 매주 보고됩니다', sv: 'Alla KPI:er spåras i realtid och rapporteras veckovis i er Kundportal', da: 'Alle KPI\'er spores i realtid og rapporteres ugentligt i din Kundeportal',
  }, lang),

  // === TechAISection ===
  techEnabledOps: (lang: string) => t({
    en: 'Tech-Enabled Operations', fr: 'Opérations technologiques', es: 'Operaciones con tecnología', ar: 'عمليات مدعومة بالتكنولوجيا',
    de: 'Technologiegestützte Operationen', it: 'Operazioni abilitate dalla tecnologia', pt: 'Operações habilitadas por tecnologia', nl: 'Technologiegestuurde operaties',
    pl: 'Operacje wspierane technologią', ru: 'Операции с поддержкой технологий', zh: '技术赋能运营', ja: 'テクノロジー対応オペレーション',
    tr: 'Teknoloji Destekli Operasyonlar', ko: '기술 기반 운영', sv: 'Teknikdrivna verksamheter', da: 'Teknologidrevne operationer',
  }, lang),

  techAIAnalytics: (lang: string) => t({
    en: 'Tech, AI & Analytics', fr: 'Tech, IA et Analytique', es: 'Tecnología, IA y Analítica', ar: 'التقنية والذكاء الاصطناعي والتحليلات',
    de: 'Technik, KI & Analytik', it: 'Tecnologia, IA & Analytics', pt: 'Tecnologia, IA e Análise', nl: 'Technologie, AI & Analytics',
    pl: 'Technologia, AI i Analityka', ru: 'Технологии, ИИ и Аналитика', zh: '技术、AI和分析', ja: 'テクノロジー、AI、アナリティクス',
    tr: 'Teknoloji, Yapay Zeka ve Analitik', ko: '기술, AI 및 분석', sv: 'Teknik, AI & Analys', da: 'Teknologi, AI & Analyse',
  }, lang),

  techAIDesc: (lang: string) => t({
    en: 'We combine human expertise with AI-powered tools to deliver faster resolutions, smarter insights, and continuous improvement.', fr: 'Nous combinons l\'expertise humaine avec des outils propulsés par l\'IA pour des résolutions plus rapides, des analyses plus intelligentes et une amélioration continue.', es: 'Combinamos la experiencia humana con herramientas impulsadas por IA para ofrecer resoluciones más rápidas, análisis más inteligentes y mejora continua.', ar: 'نجمع بين الخبرة البشرية والأدوات المدعومة بالذكاء الاصطناعي لتقديم حلول أسرع ورؤى أذكى وتحسين مستمر.',
    de: 'Wir kombinieren menschliche Expertise mit KI-gestützten Tools für schnellere Lösungen, intelligentere Erkenntnisse und kontinuierliche Verbesserung.', it: 'Combiniamo l\'esperienza umana con strumenti basati sull\'IA per risoluzioni più rapide, insight più intelligenti e miglioramento continuo.', pt: 'Combinamos expertise humana com ferramentas de IA para resoluções mais rápidas, insights mais inteligentes e melhoria contínua.', nl: 'Wij combineren menselijke expertise met AI-gestuurde tools voor snellere oplossingen, slimmere inzichten en continue verbetering.',
    pl: 'Łączymy ludzką wiedzę z narzędziami napędzanymi przez AI, aby dostarczać szybsze rozwiązania, mądrzejsze analizy i ciągłe doskonalenie.', ru: 'Мы сочетаем экспертизу людей с инструментами ИИ для более быстрого решения, умных инсайтов и непрерывного улучшения.', zh: '我们将人类专业知识与AI驱动的工具相结合，提供更快的解决方案、更智能的洞察和持续改进。', ja: '人間の専門知識とAI搭載ツールを組み合わせ、より迅速な解決、よりスマートなインサイト、継続的な改善を実現します。',
    tr: 'Daha hızlı çözümler, daha akıllı içgörüler ve sürekli iyileştirme için insan uzmanlığını AI destekli araçlarla birleştiriyoruz.', ko: '더 빠른 해결, 더 스마트한 인사이트, 지속적 개선을 위해 인간 전문성과 AI 기반 도구를 결합합니다.', sv: 'Vi kombinerar mänsklig expertis med AI-drivna verktyg för snabbare lösningar, smartare insikter och kontinuerlig förbättring.', da: 'Vi kombinerer menneskelig ekspertise med AI-drevne værktøjer for hurtigere løsninger, smartere indsigter og løbende forbedring.',
  }, lang),

  aiAssistedWorkflows: (lang: string) => t({
    en: 'AI-Assisted Workflows', fr: 'Workflows assistés par l\'IA', es: 'Flujos de trabajo asistidos por IA', ar: 'سير عمل مدعوم بالذكاء الاصطناعي',
    de: 'KI-gestützte Workflows', it: 'Workflow assistiti dall\'IA', pt: 'Fluxos de trabalho assistidos por IA', nl: 'AI-ondersteunde workflows',
    pl: 'Przepływy pracy wspomagane AI', ru: 'Рабочие процессы с ИИ', zh: 'AI辅助工作流', ja: 'AI支援ワークフロー',
    tr: 'Yapay Zeka Destekli İş Akışları', ko: 'AI 지원 워크플로', sv: 'AI-assisterade arbetsflöden', da: 'AI-assisterede arbejdsgange',
  }, lang),

  analyticsDrivenImprovements: (lang: string) => t({
    en: 'Analytics-Driven Improvements', fr: 'Améliorations basées sur l\'analytique', es: 'Mejoras impulsadas por la analítica', ar: 'تحسينات مدفوعة بالتحليلات',
    de: 'Analytikgestützte Verbesserungen', it: 'Miglioramenti basati sull\'analytics', pt: 'Melhorias baseadas em análise', nl: 'Analytisch gedreven verbeteringen',
    pl: 'Usprawnienia oparte na analizie', ru: 'Улучшения на основе аналитики', zh: '分析驱动的改进', ja: 'アナリティクス駆動の改善',
    tr: 'Analitik Odaklı İyileştirmeler', ko: '분석 기반 개선', sv: 'Analysdrivna förbättringar', da: 'Analysedrevne forbedringer',
  }, lang),

  aiDisclaimer: (lang: string) => t({
    en: 'AI tools are used to assist agents, not replace them. All AI-generated suggestions are reviewed by human agents before sending to customers. We do not use customer data to train external AI models.', fr: 'Les outils IA assistent les agents, ils ne les remplacent pas. Toutes les suggestions générées par l\'IA sont revues par des agents humains avant envoi aux clients. Nous n\'utilisons pas les données clients pour entraîner des modèles IA externes.', es: 'Las herramientas de IA se utilizan para asistir a los agentes, no para reemplazarlos. Todas las sugerencias generadas por IA son revisadas por agentes humanos antes de enviarlas a los clientes. No utilizamos datos de clientes para entrenar modelos de IA externos.', ar: 'تُستخدم أدوات الذكاء الاصطناعي لمساعدة الوكلاء وليس لاستبدالهم. يتم مراجعة جميع اقتراحات الذكاء الاصطناعي بواسطة وكلاء بشريين قبل الإرسال للعملاء. لا نستخدم بيانات العملاء لتدريب نماذج ذكاء اصطناعي خارجية.',
    de: 'KI-Tools unterstützen Agenten, ersetzen sie aber nicht. Alle KI-generierten Vorschläge werden von menschlichen Agenten geprüft. Wir nutzen keine Kundendaten zum Training externer KI-Modelle.', it: 'Gli strumenti IA assistono gli agenti, non li sostituiscono. Tutti i suggerimenti generati dall\'IA vengono rivisti dagli agenti umani prima dell\'invio ai clienti. Non utilizziamo i dati dei clienti per addestrare modelli IA esterni.', pt: 'As ferramentas de IA são usadas para assistir os agentes, não substituí-los. Todas as sugestões geradas por IA são revisadas por agentes humanos antes do envio aos clientes. Não usamos dados de clientes para treinar modelos de IA externos.', nl: 'AI-tools helpen agenten, ze vervangen ze niet. Alle door AI gegenereerde suggesties worden door menselijke agenten beoordeeld voordat ze naar klanten worden gestuurd. Wij gebruiken geen klantgegevens om externe AI-modellen te trainen.',
    pl: 'Narzędzia AI pomagają agentom, a nie ich zastępują. Wszystkie sugestie generowane przez AI są weryfikowane przez ludzkich agentów przed wysłaniem do klientów. Nie używamy danych klientów do trenowania zewnętrznych modeli AI.', ru: 'Инструменты ИИ помогают агентам, а не заменяют их. Все предложения ИИ проверяются агентами перед отправкой клиентам. Мы не используем данные клиентов для обучения внешних моделей ИИ.', zh: 'AI工具用于辅助坐席，而非替代。所有AI生成的建议在发送给客户之前都由人工坐席审核。我们不使用客户数据训练外部AI模型。', ja: 'AIツールはエージェントを支援するもので、代替するものではありません。AI生成の提案はすべて、顧客への送信前に人間のエージェントが確認します。顧客データを外部AIモデルのトレーニングに使用しません。',
    tr: 'AI araçları ajanları desteklemek için kullanılır, onların yerine geçmez. AI tarafından oluşturulan tüm öneriler müşterilere gönderilmeden önce insan ajanlar tarafından incelenir. Müşteri verilerini harici AI modellerini eğitmek için kullanmıyoruz.', ko: 'AI 도구는 상담원을 대체하는 것이 아니라 지원하는 데 사용됩니다. AI가 생성한 모든 제안은 고객에게 전송되기 전에 인간 상담원이 검토합니다. 고객 데이터를 외부 AI 모델 훈련에 사용하지 않습니다.', sv: 'AI-verktyg används för att stödja agenter, inte ersätta dem. Alla AI-genererade förslag granskas av mänskliga agenter innan de skickas till kunder. Vi använder inte kunddata för att träna externa AI-modeller.', da: 'AI-værktøjer bruges til at assistere agenter, ikke erstatte dem. Alle AI-genererede forslag gennemgås af menneskelige agenter før afsendelse til kunder. Vi bruger ikke kundedata til at træne eksterne AI-modeller.',
  }, lang),

  activeStatus: (lang: string) => t({
    en: 'Active', fr: 'Actif', es: 'Activo', ar: 'نشط',
    de: 'Aktiv', it: 'Attivo', pt: 'Ativo', nl: 'Actief',
    pl: 'Aktywny', ru: 'Активно', zh: '已启用', ja: 'アクティブ',
    tr: 'Aktif', ko: '활성', sv: 'Aktiv', da: 'Aktiv',
  }, lang),

  availability: (lang: string) => t({
    en: 'Availability', fr: 'Disponibilité', es: 'Disponibilidad', ar: 'التوفر',
    de: 'Verfügbarkeit', it: 'Disponibilità', pt: 'Disponibilidade', nl: 'Beschikbaarheid',
    pl: 'Dostępność', ru: 'Доступность', zh: '可用性', ja: '利用可能時間',
    tr: 'Müsaitlik', ko: '가용성', sv: 'Tillgänglighet', da: 'Tilgængelighed',
  }, lang),
};
